import React, { useState, useEffect, useCallback } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { 
  Activity, Lock, Mail, User, Loader2, Eye, EyeOff, 
  Settings, Key, Save, X, Zap, BarChart3, Clock, TrendingUp,
  Crown, Check, Shield, Users, RefreshCw, Search, Trash2, UserCheck, UserX, Calendar, Copy, Shuffle,
  CreditCard, Upload, QrCode, IndianRupee, CheckCircle, XCircle, Image, Phone, MessageCircle, Youtube, Send, Linkedin, Instagram,
  MessageSquare, Bell, Link, ImageIcon, CalendarClock, Table2, Maximize2, Minimize2, FileDown, FileSpreadsheet, PlayCircle, Info, AlertCircle
} from "lucide-react";
import { apiClient } from "../App";
import { toast } from "sonner";

// Import refactored components
import { MessagesTab, PermissionsTab } from "./Admin/tabs";
import FloatingContactIconsNew from "../components/common/FloatingContactIcons";

// =============================================================================
// CLIPBOARD UTILITY - Works in all browsers including iframes
// =============================================================================
const copyToClipboard = async (text, successMessage = 'Copied!') => {
  // Try modern clipboard API first (requires secure context and permissions)
  if (navigator.clipboard && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(successMessage);
      return true;
    } catch (err) {
      // Fall through to fallback
      console.log('Clipboard API failed, using fallback');
    }
  }
  
  // Fallback using textarea and execCommand
  try {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    const successful = document.execCommand('copy');
    document.body.removeChild(textArea);
    
    if (successful) {
      toast.success(successMessage);
      return true;
    } else {
      toast.error('Copy failed. Please select and copy manually.');
      return false;
    }
  } catch (err) {
    toast.error('Copy failed. Please select and copy manually.');
    return false;
  }
};

// =============================================================================
// SPLASH SCREEN
// =============================================================================
const SplashScreen = ({ onComplete }) => {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          setTimeout(onComplete, 200);
          return 100;
        }
        return prev + 2;
      });
    }, 100);
    return () => clearInterval(interval);
  }, [onComplete]);

  return (
    <div className="fixed inset-0 z-50 bg-[#030508] flex flex-col items-center justify-center overflow-hidden">
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-to-r from-cyan-500/20 via-blue-600/20 to-purple-500/20 rounded-full blur-[150px] animate-spin-slow" />
      </div>
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAwIDEwIEwgNDAgMTAgTSAxMCAwIEwgMTAgNDAgTSAwIDIwIEwgNDAgMjAgTSAyMCAwIEwgMjAgNDAgTSAwIDMwIEwgNDAgMzAgTSAzMCAwIEwgMzAgNDAiIGZpbGw9Im5vbmUiIHN0cm9rZT0iIzE1MjAzMCIgc3Ryb2tlLXdpZHRoPSIxIi8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2dyaWQpIi8+PC9zdmc+')] opacity-30" />

      <div className="relative z-10 text-center">
        <div className="relative inline-flex items-center justify-center mb-10">
          <div className="absolute inset-0 bg-gradient-to-r from-cyan-400 to-blue-500 rounded-3xl blur-2xl opacity-50 scale-125 animate-pulse" />
          <div className="relative w-32 h-32 rounded-3xl bg-gradient-to-br from-[#0a1628] to-[#061020] border-2 border-cyan-400/50 flex items-center justify-center shadow-2xl shadow-cyan-500/30">
            <Activity className="w-16 h-16 text-cyan-400" strokeWidth={1.5} />
          </div>
        </div>
        
        <h1 className="text-6xl md:text-7xl font-black bg-gradient-to-r from-white via-cyan-200 to-blue-400 bg-clip-text text-transparent tracking-tight">
          AlphaTradeVission
        </h1>
        <div className="mt-4 flex items-center justify-center gap-4">
          <span className="text-4xl md:text-5xl font-extralight text-cyan-400 tracking-wide">Ticks</span>
          <span className="px-4 py-1.5 text-sm font-bold bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-full uppercase tracking-widest shadow-lg shadow-cyan-500/40">Pro</span>
        </div>
        
        <p className="mt-8 text-2xl text-[#6b7a99] font-light tracking-wide">
          Real-time <span className="text-cyan-400 font-medium">Tick by Tick</span> Data
        </p>
        
        <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
          {[
            { icon: Zap, text: "Ultra Fast", color: "yellow" },
            { icon: BarChart3, text: "Live Analysis", color: "blue" },
            { icon: Clock, text: "Market Hours", color: "green" },
            { icon: TrendingUp, text: "Activity Scanner", color: "purple" }
          ].map((feature, i) => (
            <div 
              key={i} 
              className={`flex items-center gap-2.5 px-5 py-2.5 rounded-full border backdrop-blur-sm
                ${feature.color === 'yellow' ? 'bg-yellow-500/10 border-yellow-500/30 text-yellow-400' : ''}
                ${feature.color === 'blue' ? 'bg-blue-500/10 border-blue-500/30 text-blue-400' : ''}
                ${feature.color === 'green' ? 'bg-green-500/10 border-green-500/30 text-green-400' : ''}
                ${feature.color === 'purple' ? 'bg-purple-500/10 border-purple-500/30 text-purple-400' : ''}
              `}
            >
              <feature.icon className="w-5 h-5" />
              <span className="text-sm font-medium">{feature.text}</span>
            </div>
          ))}
        </div>

        <div className="mt-14 w-80 mx-auto">
          <div className="h-1 bg-[#1a2540] rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-cyan-400 to-blue-500 rounded-full transition-all duration-100 ease-out"
              style={{ width: `${progress}%` }}
            />
          </div>
          <p className="mt-3 text-xs text-[#4a5a7a] tracking-wider">LOADING...</p>
        </div>
      </div>

      <style>{`
        @keyframes spin-slow {
          from { transform: translate(-50%, -50%) rotate(0deg); }
          to { transform: translate(-50%, -50%) rotate(360deg); }
        }
        .animate-spin-slow { animation: spin-slow 30s linear infinite; }
      `}</style>
    </div>
  );
};

// =============================================================================
// TELEGRAM CONFIGURATION TAB COMPONENT
// =============================================================================
const TelegramConfigTab = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(null);
  const [editChannel, setEditChannel] = useState(null);
  const [formData, setFormData] = useState({ bot_token: '', chat_id: '' });
  
  const channels = [
    { id: 'channel_1', name: 'Admin Dedicated', icon: '🔷', desc: 'Algo updates, Smart Filters, Admin activity', color: 'cyan' },
    { id: 'channel_2', name: 'Security Alerts', icon: '🚨', desc: 'Wrong passwords, brute force, suspicious activity', color: 'red' },
    { id: 'channel_3', name: 'Payment & Signup', icon: '💰', desc: 'New signups, payments, UTR tracking', color: 'green' },
    { id: 'channel_4', name: 'User Activity', icon: '👤', desc: 'All user login/logout history', color: 'purple' }
  ];
  
  const fetchConfig = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/telegram-config`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setConfig(data);
      }
    } catch (err) {
      console.error('Failed to fetch telegram config:', err);
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchConfig();
  }, []);
  
  const handleEdit = (channelId) => {
    const channelKey = channelId.replace('channel_', 'channel_') + '_' + 
      (channelId === 'channel_1' ? 'admin' : 
       channelId === 'channel_2' ? 'security' : 
       channelId === 'channel_3' ? 'payment' : 'user_activity');
    const currentConfig = config?.[channelKey] || {};
    setFormData({
      bot_token: '',
      chat_id: currentConfig.chat_id || ''
    });
    setEditChannel(channelId);
  };
  
  const handleSave = async () => {
    if (!formData.bot_token || !formData.chat_id) {
      toast.error('Please fill both Bot Token and Chat ID');
      return;
    }
    
    try {
      setSaving(true);
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/telegram-config/update`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          channel: editChannel,
          bot_token: formData.bot_token,
          chat_id: formData.chat_id
        })
      });
      
      if (response.ok) {
        toast.success('Channel configured successfully!');
        setEditChannel(null);
        fetchConfig();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to save');
      }
    } catch (err) {
      toast.error('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };
  
  const handleTest = async (channelId) => {
    try {
      setTesting(channelId);
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/telegram-config/test?channel=${channelId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await response.json();
      if (data.success) {
        toast.success(`Test message sent to ${data.channel_name}!`);
      } else {
        toast.error('Failed to send test message');
      }
    } catch (err) {
      toast.error('Test failed');
    } finally {
      setTesting(null);
    }
  };
  
  const getChannelConfig = (channelId) => {
    const keyMap = {
      'channel_1': 'channel_1_admin',
      'channel_2': 'channel_2_security',
      'channel_3': 'channel_3_payment',
      'channel_4': 'channel_4_user_activity'
    };
    return config?.[keyMap[channelId]] || {};
  };
  
  if (loading) {
    return <div className="text-center py-8 text-[#6b7a99]">Loading...</div>;
  }
  
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-white flex items-center gap-2">
          <Send className="w-4 h-4 text-cyan-400" />
          4-Channel Telegram Configuration
        </h3>
        <Button size="sm" variant="ghost" onClick={fetchConfig} className="text-xs">
          <RefreshCw className="w-3 h-3 mr-1" />
          Refresh
        </Button>
      </div>
      
      <p className="text-[10px] text-[#6b7a99]">
        Configure separate Telegram channels for different alert types. Each channel can have its own bot and chat ID.
      </p>
      
      <div className="space-y-3">
        {channels.map((channel) => {
          const chConfig = getChannelConfig(channel.id);
          const isConfigured = chConfig.bot_token_set && chConfig.chat_id_set;
          const isEditing = editChannel === channel.id;
          
          return (
            <div 
              key={channel.id}
              className={`p-3 rounded-lg border ${
                isConfigured 
                  ? `bg-${channel.color}-500/10 border-${channel.color}-500/30` 
                  : 'bg-[#111827] border-[#1f2937]'
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-lg">{channel.icon}</span>
                  <div>
                    <p className="text-xs font-semibold text-white">{channel.name}</p>
                    <p className="text-[9px] text-[#6b7a99]">{channel.desc}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {isConfigured && !chConfig.fallback_to_channel_1 && (
                    <span className="text-[9px] px-2 py-0.5 bg-green-500/20 text-green-400 rounded">
                      ✓ Configured
                    </span>
                  )}
                  {chConfig.fallback_to_channel_1 && (
                    <span className="text-[9px] px-2 py-0.5 bg-amber-500/20 text-amber-400 rounded">
                      Using Ch.1
                    </span>
                  )}
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleTest(channel.id)}
                    disabled={testing === channel.id}
                    className="text-[10px] px-2 py-1 h-6"
                  >
                    {testing === channel.id ? '...' : 'Test'}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => isEditing ? setEditChannel(null) : handleEdit(channel.id)}
                    className="text-[10px] px-2 py-1 h-6"
                  >
                    {isEditing ? 'Cancel' : 'Edit'}
                  </Button>
                </div>
              </div>
              
              {chConfig.chat_id && !isEditing && (
                <p className="text-[9px] text-[#6b7a99] ml-7">
                  Chat ID: {chConfig.chat_id}
                </p>
              )}
              
              {isEditing && (
                <div className="mt-3 p-3 bg-[#0a0f1a] rounded-lg space-y-2">
                  <div>
                    <label className="text-[9px] text-[#6b7a99] block mb-1">Bot Token</label>
                    <Input
                      type="text"
                      placeholder="8110729286:AAGYp55IUHXfu..."
                      value={formData.bot_token}
                      onChange={(e) => setFormData({ ...formData, bot_token: e.target.value })}
                      className="text-xs h-7 bg-[#111827] border-[#1f2937]"
                    />
                  </div>
                  <div>
                    <label className="text-[9px] text-[#6b7a99] block mb-1">Chat ID</label>
                    <Input
                      type="text"
                      placeholder="-1003425805602"
                      value={formData.chat_id}
                      onChange={(e) => setFormData({ ...formData, chat_id: e.target.value })}
                      className="text-xs h-7 bg-[#111827] border-[#1f2937]"
                    />
                  </div>
                  <div className="flex gap-2 mt-2">
                    <Button
                      size="sm"
                      onClick={handleSave}
                      disabled={saving}
                      className="text-[10px] h-6 bg-cyan-600 hover:bg-cyan-700"
                    >
                      {saving ? 'Saving...' : 'Save & Test'}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      
      <div className="p-3 bg-[#111827] border border-[#1f2937] rounded-lg mt-4">
        <p className="text-[10px] text-[#6b7a99] mb-2">
          <strong className="text-amber-400">How to get Bot Token & Chat ID:</strong>
        </p>
        <ol className="text-[9px] text-[#6b7a99] space-y-1">
          <li>1. Create a bot via @BotFather on Telegram → Copy the <b>Bot Token</b></li>
          <li>2. Create a channel/group → Add your bot as admin</li>
          <li>3. Send a message in the channel</li>
          <li>4. Visit: https://api.telegram.org/bot[TOKEN]/getUpdates</li>
          <li>5. Find the <b>chat id</b> (negative number for groups/channels)</li>
        </ol>
      </div>
    </div>
  );
};

// =============================================================================
// ADMIN PANEL (Hidden - Password Protected)
// =============================================================================
const AdminPanel = ({ isOpen, onClose }) => {
  const [password, setPassword] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isMaximized, setIsMaximized] = useState(false); // Maximize state
  
  // Recovery mode state
  const [showRecovery, setShowRecovery] = useState(false);
  const [recoveryKey, setRecoveryKey] = useState('');
  const [newAdminPassword, setNewAdminPassword] = useState('');
  const [showNewAdminPassword, setShowNewAdminPassword] = useState(false);
  const [recoveryLoading, setRecoveryLoading] = useState(false);
  
  // Tab state
  const [activeTab, setActiveTab] = useState('credentials'); // 'credentials', 'users', 'payments', 'settings'
  
  // Credentials state
  const [apiKey, setApiKey] = useState('');
  const [apiSecret, setApiSecret] = useState('');
  const [telegramToken, setTelegramToken] = useState('');
  const [telegramChatId, setTelegramChatId] = useState('');
  const [showSecret, setShowSecret] = useState(false);
  
  // User management state
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(null); // Track which action is loading
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null); // User to delete
  const [subscriptionDays, setSubscriptionDays] = useState(30);
  const [lastSetPassword, setLastSetPassword] = useState(''); // Store the last password that was set
  
  // Payment management state
  const [pendingPayments, setPendingPayments] = useState([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [paymentFilter, setPaymentFilter] = useState('pending'); // 'pending', 'approved', 'rejected', 'all'
  
  // UPI Settings state
  const [upiId, setUpiId] = useState('');
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [paymentInstructions, setPaymentInstructions] = useState('');
  const [subscriptionPlans, setSubscriptionPlans] = useState({});
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  
  // Bank Details state
  const [bankName, setBankName] = useState('');
  const [accountHolder, setAccountHolder] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [ifscCode, setIfscCode] = useState('');
  const [showBankDetailsAdmin, setShowBankDetailsAdmin] = useState(false);
  
  // Zerodha Setup Video state
  const [zerodhaSetupVideo, setZerodhaSetupVideo] = useState('');
  
  // Contact Settings state
  const [whatsappNumber, setWhatsappNumber] = useState('');
  const [whatsappMessage, setWhatsappMessage] = useState('');
  const [phoneNumberContact, setPhoneNumberContact] = useState('');
  const [phoneBusyMessage, setPhoneBusyMessage] = useState('');
  const [emailAddress, setEmailAddress] = useState('');
  const [telegramGroup, setTelegramGroup] = useState('');
  const [youtubeChannel, setYoutubeChannel] = useState('');
  const [facebookPage, setFacebookPage] = useState('');
  const [linkedinProfile, setLinkedinProfile] = useState('');
  const [instagramProfile, setInstagramProfile] = useState('');
  const [welcomeMessage, setWelcomeMessage] = useState('');
  const [showContactsEnabled, setShowContactsEnabled] = useState(true);
  
  // Algo Setup state
  const [algoSetupConfigs, setAlgoSetupConfigs] = useState([]);
  const [algoSetupLoading, setAlgoSetupLoading] = useState(false);
  const defaultSheetConfig = (index) => ({
    url: '',
    tab_name: `Sheet ${index}`,
    google_sheet_tab: '',
    is_active: false,
    hidden_columns: [],
    show_row_numbers: true,
    hiddenColsInput: '',
    alerts_enabled: true,
    alert_columns: [],
    alertColsInput: '',
    alert_sound: true,
    highlight_changes: true
  });
  const [algoSheets, setAlgoSheets] = useState([
    { ...defaultSheetConfig(1), is_active: true },
    { ...defaultSheetConfig(2), is_active: true },
    defaultSheetConfig(3),
    defaultSheetConfig(4),
    defaultSheetConfig(5)
  ]);
  
  // Helper to update a specific sheet
  const updateAlgoSheet = (index, updates) => {
    setAlgoSheets(prev => {
      const newSheets = [...prev];
      newSheets[index] = { ...newSheets[index], ...updates };
      return newSheets;
    });
  };
  
  // Reports export state
  const [exportLoading, setExportLoading] = useState(false);
  const [exportDate, setExportDate] = useState(new Date().toISOString().split('T')[0]); // Today's date
  
  // Admin Panel Security
  const [adminSecurityEnabled, setAdminSecurityEnabled] = useState(() => {
    try {
      return localStorage.getItem('admin_security_enabled') === 'true';
    } catch {
      return false;
    }
  });
  const [adminSecurityCode, setAdminSecurityCode] = useState(() => {
    try {
      return localStorage.getItem('admin_security_code') || '';
    } catch {
      return '';
    }
  });
  
  // Affiliates/Influencers state
  const [influencers, setInfluencers] = useState([]);
  const [influencersLoading, setInfluencersLoading] = useState(false);
  const [showAddInfluencer, setShowAddInfluencer] = useState(false);
  const [editingInfluencer, setEditingInfluencer] = useState(null);
  const [newInfluencer, setNewInfluencer] = useState({
    name: '',
    promo_code: '',
    email: '',
    phone: '',
    commission_percent: 10,
    is_lifetime_partner: false
  });
  const [referralTransactions, setReferralTransactions] = useState([]);
  const [payoutAmount, setPayoutAmount] = useState('');
  const [payoutNotes, setPayoutNotes] = useState('');
  const [adminEmail, setAdminEmail] = useState('alphatradevission@gmail.com');
  
  // Admin Email loading/saving for readymade layout permissions
  const [adminEmailLoading, setAdminEmailLoading] = useState(false);
  
  const loadAdminEmail = useCallback(async (pwd) => {
    try {
      const response = await apiClient.get(`/admin/admin-email?password=${encodeURIComponent(pwd)}`);
      if (response.data.success && response.data.admin_email) {
        setAdminEmail(response.data.admin_email);
      }
    } catch (err) {
      console.warn('Failed to load admin email:', err.message);
    }
  }, []);
  
  const saveAdminEmail = useCallback(async () => {
    if (!adminEmail.trim()) {
      toast.error('Please enter an admin email');
      return;
    }
    setAdminEmailLoading(true);
    try {
      const response = await apiClient.post('/admin/admin-email', {
        admin_password: password,
        admin_email: adminEmail.trim()
      });
      if (response.data.success) {
        toast.success(`Admin email set to ${adminEmail.trim()}`);
      } else {
        toast.error(response.data.error || 'Failed to save admin email');
      }
    } catch (err) {
      toast.error('Failed to save admin email');
    } finally {
      setAdminEmailLoading(false);
    }
  }, [adminEmail, password]);

  
  // Consolidated User Referrals state
  const [allReferrals, setAllReferrals] = useState({ referrers: [], summary: {} });
  const [allReferralsLoading, setAllReferralsLoading] = useState(false);
  const [showAllReferrals, setShowAllReferrals] = useState(false);
  
  // Messages/Broadcast state
  const [broadcasts, setBroadcasts] = useState([]);
  const [broadcastsLoading, setBroadcastsLoading] = useState(false);
  const [showCreateBroadcast, setShowCreateBroadcast] = useState(false);
  const [newBroadcast, setNewBroadcast] = useState({
    title: '',
    message: '',
    image_url: '',
    link_url: '',
    link_text: '',
    target_audience: 'all',
    scheduled_date: '',
    scheduled_time: ''
  });
  const [expirySettings, setExpirySettings] = useState({
    days_before_expiry: 3,
    pre_expiry_message: 'Hi {name}! Your AlphaTradeVission subscription expires in {days} days. Renew now!',
    post_expiry_message: 'Hi {name}! Your AlphaTradeVission subscription has expired. Renew now to get back access!',
    enabled: true
  });
  const [expiringUsers, setExpiringUsers] = useState({ expiring_soon: [], recently_expired: [] });
  const [showExpiryReminders, setShowExpiryReminders] = useState(false);
  const [broadcastLinks, setBroadcastLinks] = useState(null);
  
  const redirectUrl = `${window.location.origin}/api/auth/zerodha/callback`;
  
  // Handle admin password recovery
  const handleRecoverPassword = async () => {
    if (!recoveryKey.trim()) {
      toast.error('Please enter the Master Recovery Key');
      return;
    }
    if (newAdminPassword.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }
    
    setRecoveryLoading(true);
    try {
      const response = await apiClient.post('/admin/recover', {
        recovery_key: recoveryKey,
        new_password: newAdminPassword
      });
      
      if (response.data.success) {
        toast.success('Admin password reset successfully!', { duration: 5000 });
        // Set the new password in the login field
        setPassword(newAdminPassword);
        setShowRecovery(false);
        setRecoveryKey('');
        setNewAdminPassword('');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid recovery key');
    }
    setRecoveryLoading(false);
  };
  
  // Generate a random password
  const generatePassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$%';
    let password = '';
    for (let i = 0; i < 10; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setNewPassword(password);
    setShowNewPassword(true); // Auto-show generated password
    toast.success('Password generated! Click copy to save it.');
  };
  
  // Copy password to clipboard
  const copyPassword = async (pwd) => {
    copyToClipboard(pwd, 'Password copied to clipboard!');
  };

  // Download daily activity report as Excel
  const downloadDailyActivityReport = async () => {
    setExportLoading(true);
    try {
      // Use admin password for authentication
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/export/daily-activity?date=${exportDate}&admin_password=${encodeURIComponent(password)}`,
        {
          method: 'GET',
        }
      );
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to generate report');
      }
      
      // Get the blob
      const blob = await response.blob();
      const filename = `daily_activity_${exportDate}.xlsx`;
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success(`Report downloaded: ${filename}`);
    } catch (error) {
      console.error('Export error:', error);
      toast.error(error.message || 'Failed to download report');
    }
    setExportLoading(false);
  };

  const verifyPassword = async () => {
    setIsLoading(true);
    setError('');
    try {
      const response = await apiClient.post('/admin/verify', { password });
      if (response.data.success) {
        setIsAuthenticated(true);
        const credsResponse = await apiClient.get(`/admin/credentials?password=${password}`);
        setApiKey(credsResponse.data.api_key || '');
        setApiSecret('');
        setTelegramToken('');
        setTelegramChatId(credsResponse.data.telegram_chat_id || '');
        // Load admin email setting
        loadAdminEmail(password);
      } else {
        setError('Invalid password');
      }
    } catch (err) {
      setError('Authentication failed');
    }
    setIsLoading(false);
  };

  const saveCredentials = async () => {
    setIsLoading(true);
    setError('');
    setSuccess('');
    try {
      const response = await apiClient.post('/admin/credentials', {
        admin_password: password,
        api_key: apiKey || undefined,
        api_secret: apiSecret || undefined,
        telegram_bot_token: telegramToken || undefined,
        telegram_chat_id: telegramChatId || undefined
      });
      if (response.data.success) {
        setSuccess('Credentials saved successfully!');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(response.data.message);
      }
    } catch (err) {
      setError('Failed to save credentials');
    }
    setIsLoading(false);
  };

  const handleZerodhaLogin = async () => {
    try {
      const response = await apiClient.get('/auth/zerodha-login-url');
      if (response.data.login_url) {
        window.location.href = response.data.login_url;
      }
    } catch (err) {
      setError('Failed to get login URL');
    }
  };
  
  // Fetch users when switching to users tab
  const fetchUsers = async () => {
    setUsersLoading(true);
    try {
      const response = await apiClient.get(`/admin/users?password=${password}`);
      if (response.data.success) {
        setUsers(response.data.users);
      }
    } catch (err) {
      toast.error('Failed to fetch users');
    }
    setUsersLoading(false);
  };
  
  // Reset user password
  const handleResetPassword = async () => {
    if (!selectedUser || !newPassword) {
      toast.error('Please select a user and enter a new password');
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setResetLoading(true);
    try {
      const response = await apiClient.post('/admin/reset-password', {
        admin_password: password,
        user_email: selectedUser.email,
        new_password: newPassword
      });
      
      if (response.data.success) {
        // Store the password that was set so admin can see/copy it
        setLastSetPassword(newPassword);
        toast.success(`Password reset for ${selectedUser.email}`, {
          description: `New password: ${newPassword}`,
          duration: 10000
        });
        // Don't clear selectedUser so admin can see the last set password
        setNewPassword('');
      } else {
        toast.error(response.data.message || 'Failed to reset password');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset password');
    }
    setResetLoading(false);
  };
  
  // Handle user actions (activate/deactivate/delete)
  const handleUserAction = async (action, user) => {
    setActionLoading(`${action}-${user.email}`);
    try {
      const response = await apiClient.post('/admin/user/update', {
        admin_password: password,
        user_email: user.email,
        action: action
      });
      
      if (response.data.success) {
        toast.success(response.data.message);
        fetchUsers(); // Refresh list
        if (action === 'delete') {
          setShowDeleteConfirm(null);
        }
      } else {
        toast.error(response.data.message || `Failed to ${action} user`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to ${action} user`);
    }
    setActionLoading(null);
  };
  
  // Grant subscription to user
  const handleGrantSubscription = async (user) => {
    setActionLoading(`subscription-${user.email}`);
    try {
      const response = await apiClient.post('/admin/user/update', {
        admin_password: password,
        user_email: user.email,
        action: 'set_subscription',
        subscription_status: 'active',
        subscription_plan: 'admin_granted',
        subscription_days: subscriptionDays
      });
      
      if (response.data.success) {
        toast.success(`Granted ${subscriptionDays} days subscription to ${user.email}`);
        fetchUsers(); // Refresh list
        setSelectedUser(null);
      } else {
        toast.error(response.data.message || 'Failed to grant subscription');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to grant subscription');
    }
    setActionLoading(null);
  };
  
  // Fetch pending payments
  const fetchPayments = async (status = 'pending') => {
    setPaymentsLoading(true);
    try {
      const endpoint = status === 'pending' ? '/payment/pending' : '/payment/all';
      const params = status === 'pending' ? { admin_password: password } : { admin_password: password, status: status === 'all' ? undefined : status };
      const response = await apiClient.get(endpoint, { params });
      setPendingPayments(response.data.payments || []);
    } catch (err) {
      toast.error('Failed to fetch payments');
    }
    setPaymentsLoading(false);
  };
  
  // Verify/Reject payment
  const handlePaymentAction = async (paymentId, action, reason = '') => {
    setActionLoading(`payment-${paymentId}`);
    try {
      const response = await apiClient.post('/payment/verify', {
        admin_password: password,
        payment_id: paymentId,
        action: action,
        rejection_reason: reason
      });
      if (response.data.success) {
        toast.success(response.data.message);
        fetchPayments(paymentFilter);
        fetchUsers(); // Refresh users as their status might have changed
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to ${action} payment`);
    }
    setActionLoading(null);
  };
  
  // Fetch UPI settings
  const fetchSettings = async () => {
    setSettingsLoading(true);
    try {
      // Get all settings including bank details (need admin endpoint)
      const plansResponse = await apiClient.get('/payment/plans', { params: { admin_password: password } });
      setSubscriptionPlans(plansResponse.data.plans || {});
      
      // Fetch full settings with bank details
      const settingsResponse = await apiClient.post('/payment/settings/update', {
        admin_password: password
      });
      const data = settingsResponse.data.data || {};
      setUpiId(data.upi_id || '');
      setQrCodeUrl(data.qr_code_url || '');
      setPaymentInstructions(data.payment_instructions || '');
      setBankName(data.bank_name || '');
      setAccountHolder(data.account_holder || '');
      setAccountNumber(data.account_number || '');
      setIfscCode(data.ifsc_code || '');
      setShowBankDetailsAdmin(data.show_bank_details || false);
      setZerodhaSetupVideo(data.zerodha_setup_video || '');
    } catch (err) {
      // Fallback to public settings
      try {
        const response = await apiClient.get('/payment/settings');
        setUpiId(response.data.upi_id || '');
        setQrCodeUrl(response.data.qr_code_url || '');
        setPaymentInstructions(response.data.payment_instructions || '');
      } catch (e) {
        console.error('Failed to fetch settings:', e);
      }
    }
    setSettingsLoading(false);
  };
  
  // Update UPI settings
  const saveUpiSettings = async () => {
    setSettingsLoading(true);
    try {
      const response = await apiClient.post('/payment/settings/update', {
        admin_password: password,
        upi_id: upiId,
        qr_code_url: qrCodeUrl,
        payment_instructions: paymentInstructions,
        bank_name: bankName,
        account_holder: accountHolder,
        account_number: accountNumber,
        ifsc_code: ifscCode,
        show_bank_details: showBankDetailsAdmin,
        zerodha_setup_video: zerodhaSetupVideo
      });
      if (response.data.success) {
        toast.success('Payment settings saved!');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save settings');
    }
    setSettingsLoading(false);
  };
  
  // Fetch contact settings
  const fetchContactSettings = async () => {
    try {
      const response = await apiClient.post('/contact/settings/update', {
        admin_password: password
      });
      const data = response.data.data || {};
      setWhatsappNumber(data.whatsapp_number || '');
      setWhatsappMessage(data.whatsapp_message || '');
      setPhoneNumberContact(data.phone_number || '');
      setPhoneBusyMessage(data.phone_busy_message || '');
      setEmailAddress(data.email_address || '');
      setTelegramGroup(data.telegram_group || '');
      setYoutubeChannel(data.youtube_channel || '');
      setFacebookPage(data.facebook_page || '');
      setLinkedinProfile(data.linkedin_profile || '');
      setInstagramProfile(data.instagram_profile || '');
      setWelcomeMessage(data.welcome_message || '');
      setShowContactsEnabled(data.show_contacts !== false);
    } catch (err) {
      // Use defaults
      try {
        const response = await apiClient.get('/contact/settings');
        setWhatsappNumber(response.data.whatsapp_number || '');
        setWhatsappMessage(response.data.whatsapp_message || '');
        setPhoneNumberContact(response.data.phone_number || '');
        setPhoneBusyMessage(response.data.phone_busy_message || '');
        setEmailAddress(response.data.email_address || '');
        setTelegramGroup(response.data.telegram_group || '');
        setYoutubeChannel(response.data.youtube_channel || '');
        setFacebookPage(response.data.facebook_page || '');
        setLinkedinProfile(response.data.linkedin_profile || '');
        setInstagramProfile(response.data.instagram_profile || '');
        setWelcomeMessage(response.data.welcome_message || '');
        setShowContactsEnabled(response.data.show_contacts !== false);
      } catch (e) {
        console.error('Failed to fetch contact settings:', e);
      }
    }
  };
  
  // Save contact settings
  const saveContactSettings = async () => {
    setSettingsLoading(true);
    try {
      const response = await apiClient.post('/contact/settings/update', {
        admin_password: password,
        whatsapp_number: whatsappNumber,
        whatsapp_message: whatsappMessage,
        phone_number: phoneNumberContact,
        phone_busy_message: phoneBusyMessage,
        email_address: emailAddress,
        telegram_group: telegramGroup,
        youtube_channel: youtubeChannel,
        facebook_page: facebookPage,
        linkedin_profile: linkedinProfile,
        instagram_profile: instagramProfile,
        welcome_message: welcomeMessage,
        show_contacts: showContactsEnabled
      });
      if (response.data.success) {
        toast.success('Contact settings saved!');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save contact settings');
    }
    setSettingsLoading(false);
  };
  
  // Fetch influencers/affiliates
  const fetchInfluencers = async () => {
    setInfluencersLoading(true);
    try {
      const response = await apiClient.get(`/admin/influencers?admin_password=${encodeURIComponent(password)}`);
      setInfluencers(response.data.influencers || []);
      // Also fetch recent transactions
      const txnResponse = await apiClient.get(`/admin/referral-transactions?admin_password=${encodeURIComponent(password)}&limit=50`);
      setReferralTransactions(txnResponse.data.transactions || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load influencers');
    }
    setInfluencersLoading(false);
  };
  
  // Fetch all user-to-user referrals (consolidated view)
  const fetchAllReferrals = async () => {
    setAllReferralsLoading(true);
    try {
      const response = await apiClient.get(`/admin/all-referrals?admin_password=${encodeURIComponent(password)}&limit=200`);
      setAllReferrals(response.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load referrals');
    }
    setAllReferralsLoading(false);
  };
  
  // Fetch Algo Setup configurations
  const fetchAlgoSetupConfigs = async () => {
    setAlgoSetupLoading(true);
    try {
      const response = await apiClient.get(`/admin/algo-setup/config?admin_password=${encodeURIComponent(password)}`);
      if (response.data.success) {
        const configs = response.data.configs || [];
        setAlgoSetupConfigs(configs);
        
        // Update all 5 sheets from configs
        setAlgoSheets(prev => {
          const newSheets = [...prev];
          for (let i = 0; i < 5; i++) {
            const sheetConfig = configs.find(c => c.sheet_index === (i + 1));
            if (sheetConfig) {
              const hiddenCols = sheetConfig.hidden_columns || [];
              const alertCols = sheetConfig.alert_columns || [];
              newSheets[i] = {
                url: sheetConfig.sheet_url || '',
                tab_name: sheetConfig.tab_name || `Sheet ${i + 1}`,
                google_sheet_tab: sheetConfig.google_sheet_tab || '',
                is_active: sheetConfig.is_active ?? (i < 2),
                hidden_columns: hiddenCols,
                show_row_numbers: sheetConfig.show_row_numbers ?? true,
                hiddenColsInput: hiddenCols.map(c => c + 1).join(', '),
                alerts_enabled: sheetConfig.alerts_enabled ?? true,
                alert_columns: alertCols,
                alertColsInput: alertCols.map(c => c + 1).join(', '),
                alert_sound: sheetConfig.alert_sound ?? true,
                highlight_changes: sheetConfig.highlight_changes ?? true
              };
            }
          }
          return newSheets;
        });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load Algo Setup configs');
    }
    setAlgoSetupLoading(false);
  };
  
  // Save Algo Setup sheet config
  const saveAlgoSheetConfig = async (sheetIndex, config) => {
    setAlgoSetupLoading(true);
    try {
      // Parse hidden columns from input (1-based user input to 0-based for API)
      const hiddenCols = config.hiddenColsInput 
        ? config.hiddenColsInput.split(',').map(s => parseInt(s.trim()) - 1).filter(n => !isNaN(n) && n >= 0)
        : [];
      
      // Parse alert columns from input (1-based user input to 0-based for API)
      const alertCols = config.alertColsInput 
        ? config.alertColsInput.split(',').map(s => parseInt(s.trim()) - 1).filter(n => !isNaN(n) && n >= 0)
        : [];
      
      const response = await apiClient.post('/admin/algo-setup/config', {
        admin_password: password,
        sheet_index: sheetIndex,
        sheet_url: config.url,
        tab_name: config.tab_name,
        google_sheet_tab: config.google_sheet_tab || '',
        is_active: config.is_active,
        hidden_columns: hiddenCols,
        show_row_numbers: config.show_row_numbers,
        alerts_enabled: config.alerts_enabled,
        alert_columns: alertCols,
        alert_sound: config.alert_sound,
        highlight_changes: config.highlight_changes
      });
      if (response.data.success) {
        toast.success(`Sheet ${sheetIndex} saved successfully!`);
        fetchAlgoSetupConfigs();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save sheet config');
    }
    setAlgoSetupLoading(false);
  };
  
  // Test Google Sheet connection
  const [testingSheet, setTestingSheet] = useState(null);
  const testGoogleSheetConnection = async (sheetIndex, config) => {
    setTestingSheet(sheetIndex);
    try {
      const response = await apiClient.post('/admin/algo-setup/test-sheet', {
        admin_password: password,
        sheet_url: config.url,
        google_sheet_tab: config.google_sheet_tab || ''
      });
      
      if (response.data.success) {
        toast.success(response.data.message);
        console.log('Sheet test result:', response.data);
      } else {
        toast.error(response.data.message || 'Cannot access Google Sheet');
        console.error('Sheet test failed:', response.data);
      }
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.response?.data?.detail || 'Failed to test sheet connection';
      toast.error(errorMsg);
      console.error('Sheet test error:', err);
    }
    setTestingSheet(null);
  };
  
  // Create new influencer
  const createInfluencer = async () => {
    if (!newInfluencer.name || !newInfluencer.promo_code) {
      toast.error('Please enter name and promo code');
      return;
    }
    setInfluencersLoading(true);
    try {
      const response = await apiClient.post('/admin/influencer/create', {
        admin_password: password,
        ...newInfluencer
      });
      if (response.data.success) {
        toast.success(`Influencer "${newInfluencer.name}" created!`);
        setShowAddInfluencer(false);
        setNewInfluencer({ name: '', promo_code: '', commission_percent: 10, is_lifetime_partner: false });
        fetchInfluencers();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create influencer');
    }
    setInfluencersLoading(false);
  };
  
  // Update influencer
  const updateInfluencer = async (influencer) => {
    setInfluencersLoading(true);
    try {
      const response = await apiClient.post('/admin/influencer/update', {
        admin_password: password,
        influencer_id: influencer.influencer_id,
        name: influencer.name,
        promo_code: influencer.promo_code,
        commission_percent: influencer.commission_percent,
        is_lifetime_partner: influencer.is_lifetime_partner,
        is_active: influencer.is_active
      });
      if (response.data.success) {
        toast.success('Influencer updated!');
        setEditingInfluencer(null);
        fetchInfluencers();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update influencer');
    }
    setInfluencersLoading(false);
  };
  
  // Delete influencer
  const deleteInfluencer = async (influencerId) => {
    if (!window.confirm('Are you sure you want to delete this influencer?')) return;
    try {
      const response = await apiClient.delete(`/admin/influencer/${influencerId}?admin_password=${encodeURIComponent(password)}`);
      if (response.data.success) {
        toast.success('Influencer deleted');
        fetchInfluencers();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete influencer');
    }
  };
  
  // Mark payout as paid
  const markInfluencerPaid = async (influencerId) => {
    const amount = parseInt(payoutAmount);
    if (!amount || amount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    try {
      const response = await apiClient.post('/admin/influencer/mark-paid', {
        admin_password: password,
        influencer_id: influencerId,
        amount: amount,
        notes: payoutNotes
      });
      if (response.data.success) {
        toast.success(`₹${amount} marked as paid`);
        setPayoutAmount('');
        setPayoutNotes('');
        fetchInfluencers();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to mark payout');
    }
  };
  
  // Update subscription plan
  const savePlan = async (planId, planData) => {
    try {
      const response = await apiClient.post('/payment/plans/update', {
        admin_password: password,
        plan_id: planId,
        ...planData
      });
      if (response.data.success) {
        toast.success('Plan updated!');
        setSubscriptionPlans(response.data.plans);
        setEditingPlan(null);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update plan');
    }
  };
  
  // Export referral data to CSV (all influencers)
  const exportReferralCSV = () => {
    if (influencers.length === 0 && referralTransactions.length === 0) {
      toast.error('No data to export');
      return;
    }
    
    // Create CSV content
    let csvContent = "data:text/csv;charset=utf-8,";
    
    // Influencers section
    csvContent += "=== INFLUENCERS/AFFILIATES ===\n";
    csvContent += "Name,Promo Code,Email,Phone,Commission %,Lifetime Partner,Total Referrals,Total Revenue,Total Earned,Total Paid,Pending\n";
    influencers.forEach(inf => {
      csvContent += `"${inf.name}","${inf.promo_code}","${inf.email || ''}","${inf.phone || ''}",${inf.commission_percent}%,${inf.is_lifetime_partner ? 'Yes' : 'No'},${inf.total_referrals},Rs.${inf.total_revenue_generated},Rs.${inf.total_commission_earned},Rs.${inf.total_commission_paid},Rs.${inf.pending_commission}\n`;
    });
    
    csvContent += "\n=== TRANSACTIONS ===\n";
    csvContent += "Date,Type,User Email,Promo Code,Amount,Commission %,Commission Amount,Status\n";
    referralTransactions.forEach(txn => {
      const date = new Date(txn.created_at).toLocaleDateString();
      csvContent += `"${date}","${txn.type}","${txn.referee_email}","${txn.promo_code || 'N/A'}",Rs.${txn.payment_amount || 0},${txn.commission_percent || 0}%,Rs.${txn.commission_amount || 0},"${txn.status}"\n`;
    });
    
    // Download
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `referral_report_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('CSV exported successfully!');
  };
  
  // Export CSV for specific influencer
  const exportInfluencerCSV = async (influencerId, influencerName) => {
    try {
      const response = await apiClient.get(`/admin/influencer/${influencerId}/csv?admin_password=${encodeURIComponent(password)}`);
      if (response.data.success) {
        const csvContent = "data:text/csv;charset=utf-8," + response.data.csv_content;
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", `${influencerName.replace(/\s+/g, '_')}_report_${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success(`Report exported for ${influencerName}`);
      }
    } catch (err) {
      toast.error('Failed to export report');
    }
  };
  
  // Send report to influencer via WhatsApp/Email
  const sendInfluencerReport = async (influencerId) => {
    try {
      const response = await apiClient.post('/admin/influencer/send-report', {
        admin_password: password,
        influencer_id: influencerId
      });
      
      if (response.data.success) {
        // Show options modal
        const inf = response.data;
        let message = `Report generated for ${inf.influencer_name}\n\n`;
        
        if (inf.whatsapp_link) {
          message += `WhatsApp: Click to send via WhatsApp\n`;
        }
        if (inf.gmail_link) {
          message += `Gmail: Click to compose email\n`;
        }
        
        // Open action modal or directly open links
        if (inf.gmail_link) {
          window.open(inf.gmail_link, '_blank');
          toast.success('Gmail compose window opened!');
        } else if (inf.whatsapp_link) {
          window.open(inf.whatsapp_link, '_blank');
          toast.success('WhatsApp opened!');
        } else {
          toast.info('Please add email or phone to the influencer first');
        }
      }
    } catch (err) {
      toast.error('Failed to generate report');
    }
  };
  
  // Send WhatsApp message to influencer
  const sendWhatsAppReport = async (influencerId) => {
    try {
      const response = await apiClient.post('/admin/influencer/send-report', {
        admin_password: password,
        influencer_id: influencerId
      });
      
      if (response.data.success && response.data.whatsapp_link) {
        window.open(response.data.whatsapp_link, '_blank');
        toast.success('WhatsApp opened!');
      } else {
        toast.info('Please add phone number to the influencer first');
      }
    } catch (err) {
      toast.error('Failed to send WhatsApp');
    }
  };
  
  // =============================================================================
  // BROADCAST/MESSAGES FUNCTIONS
  // =============================================================================
  
  // Fetch broadcasts
  const fetchBroadcasts = async () => {
    setBroadcastsLoading(true);
    try {
      const [broadcastsRes, settingsRes, expiringRes] = await Promise.all([
        apiClient.get(`/admin/broadcasts?admin_password=${encodeURIComponent(password)}`),
        apiClient.get(`/admin/expiry-notification-settings?admin_password=${encodeURIComponent(password)}`),
        apiClient.get(`/admin/users-expiring?admin_password=${encodeURIComponent(password)}&days=7`)
      ]);
      setBroadcasts(broadcastsRes.data.broadcasts || []);
      setExpirySettings(settingsRes.data);
      setExpiringUsers(expiringRes.data);
    } catch (err) {
      console.error('Failed to fetch broadcasts:', err);
    }
    setBroadcastsLoading(false);
  };
  
  // Create new broadcast
  const createBroadcast = async () => {
    if (!newBroadcast.title || !newBroadcast.message) {
      toast.error('Please enter title and message');
      return;
    }
    try {
      const response = await apiClient.post('/admin/broadcast/create', {
        admin_password: password,
        ...newBroadcast
      });
      if (response.data.success) {
        toast.success(response.data.message);
        setShowCreateBroadcast(false);
        setNewBroadcast({
          title: '', message: '', image_url: '', link_url: '', link_text: '',
          target_audience: 'all', scheduled_date: '', scheduled_time: ''
        });
        fetchBroadcasts();
      }
    } catch (err) {
      toast.error('Failed to create broadcast');
    }
  };
  
  // Delete broadcast
  const deleteBroadcast = async (broadcastId) => {
    if (!window.confirm('Delete this broadcast?')) return;
    try {
      await apiClient.delete(`/admin/broadcast/${broadcastId}?admin_password=${encodeURIComponent(password)}`);
      toast.success('Broadcast deleted');
      fetchBroadcasts();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };
  
  // Send broadcast (get WhatsApp links)
  const sendBroadcast = async (broadcastId) => {
    try {
      const response = await apiClient.post(`/admin/broadcast/${broadcastId}/send?admin_password=${encodeURIComponent(password)}`);
      if (response.data.success) {
        setBroadcastLinks(response.data);
        toast.success(`Ready to send to ${response.data.total_users} users!`);
      }
    } catch (err) {
      toast.error('Failed to prepare broadcast');
    }
  };
  
  // Save expiry notification settings
  const saveExpirySettings = async () => {
    try {
      await apiClient.post('/admin/expiry-notification-settings', {
        admin_password: password,
        ...expirySettings
      });
      toast.success('Expiry notification settings saved!');
    } catch (err) {
      toast.error('Failed to save settings');
    }
  };
  
  // Get expiry reminder links
  const getExpiryReminders = async () => {
    try {
      const response = await apiClient.post(`/admin/send-expiry-reminders?admin_password=${encodeURIComponent(password)}`);
      if (response.data.success) {
        setShowExpiryReminders(true);
        setExpiringUsers({
          expiring_soon: response.data.pre_expiry.users,
          recently_expired: response.data.post_expiry.users
        });
        toast.success(`Found ${response.data.pre_expiry.count} expiring and ${response.data.post_expiry.count} expired users`);
      }
    } catch (err) {
      toast.error('Failed to get reminders');
    }
  };
  
  // Filter users based on search
  const filteredUsers = users.filter(user => 
    user.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.phone?.includes(searchQuery)
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm">
      <div className={`relative bg-[#0a0e17] border border-[#1a2540] rounded-2xl shadow-2xl overflow-y-auto transition-all duration-300 ${
        isMaximized 
          ? 'w-full h-full max-w-full max-h-full rounded-none' 
          : 'w-full max-w-2xl max-h-[90vh]'
      }`}>
        <div className="flex items-center justify-between p-4 border-b border-[#1a2540] sticky top-0 bg-[#0a0e17] z-10">
          <h2 className="text-lg font-bold text-white flex items-center gap-2">
            <Settings className="w-5 h-5 text-cyan-400" />
            Owner Admin Panel
          </h2>
          <div className="flex items-center gap-2">
            <button 
              onClick={() => setIsMaximized(!isMaximized)} 
              className="text-[#6b7a99] hover:text-cyan-400 p-1 rounded hover:bg-[#1a2540] transition-colors"
              title={isMaximized ? "Minimize" : "Maximize"}
            >
              {isMaximized ? (
                <Minimize2 className="w-4 h-4" />
              ) : (
                <Maximize2 className="w-4 h-4" />
              )}
            </button>
            <button onClick={onClose} className="text-[#6b7a99] hover:text-white p-1">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="p-5">
          {!isAuthenticated ? (
            <div className="space-y-4">
              <p className="text-sm text-[#6b7a99]">Enter owner password to access admin panel</p>
              <div>
                <Label className="text-[#6b7a99] text-xs">Password</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4a5a7a]" />
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && verifyPassword()}
                    className="pl-10 bg-[#111827] border-[#1a2540] text-white rounded-lg"
                    placeholder="Enter owner password"
                    data-testid="admin-password-input"
                  />
                </div>
              </div>
              {error && <p className="text-xs text-red-400">{error}</p>}
              <Button
                onClick={verifyPassword}
                disabled={isLoading || !password}
                className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-semibold rounded-lg"
                data-testid="admin-verify-btn"
              >
                {isLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Key className="w-4 h-4 mr-2" />}
                Verify
              </Button>
              
              {/* Forgot Password Link */}
              <div className="text-center pt-2">
                <button
                  onClick={() => setShowRecovery(true)}
                  className="text-xs text-cyan-400 hover:text-cyan-300 hover:underline transition-colors"
                  data-testid="forgot-password-link"
                >
                  Forgot Password?
                </button>
              </div>
              
              {/* Recovery Modal */}
              {showRecovery && (
                <div className="mt-4 p-4 bg-[#111827] border border-amber-500/30 rounded-lg space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-amber-400">Reset Admin Password</h3>
                    <button
                      onClick={() => {
                        setShowRecovery(false);
                        setRecoveryKey('');
                        setNewAdminPassword('');
                      }}
                      className="text-[#6b7a99] hover:text-white"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <p className="text-[10px] text-[#6b7a99]">
                    Enter the Master Recovery Key to reset the admin password.
                  </p>
                  <div>
                    <Label className="text-[#6b7a99] text-xs">Master Recovery Key</Label>
                    <Input
                      type="password"
                      value={recoveryKey}
                      onChange={(e) => setRecoveryKey(e.target.value)}
                      className="mt-1 bg-[#0a0e17] border-[#1a2540] text-white rounded-lg text-sm"
                      placeholder="Enter master key"
                      data-testid="recovery-key-input"
                    />
                  </div>
                  <div>
                    <Label className="text-[#6b7a99] text-xs">New Admin Password</Label>
                    <Input
                      type="text"
                      value={newAdminPassword}
                      onChange={(e) => setNewAdminPassword(e.target.value)}
                      className="mt-1 bg-[#0a0e17] border-[#1a2540] text-white rounded-lg text-sm"
                      placeholder="Enter new password (min 6 chars)"
                      data-testid="new-admin-password-input"
                    />
                  </div>
                  <Button
                    onClick={handleRecoverPassword}
                    disabled={recoveryLoading || !recoveryKey || !newAdminPassword}
                    className="w-full bg-gradient-to-r from-amber-500 to-orange-600 text-white font-semibold rounded-lg"
                    data-testid="reset-admin-password-btn"
                  >
                    {recoveryLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Key className="w-4 h-4 mr-2" />}
                    Reset Admin Password
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {/* Tabs */}
              <div className="flex flex-wrap gap-2 border-b border-[#1a2540] pb-3">
                <button
                  onClick={() => setActiveTab('credentials')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                    activeTab === 'credentials' 
                      ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/40' 
                      : 'text-[#6b7a99] hover:text-white hover:bg-[#111827]'
                  }`}
                  data-testid="admin-credentials-tab"
                >
                  <Key className="w-3 h-3 inline mr-1" />
                  API
                </button>
                <button
                  onClick={() => {
                    setActiveTab('users');
                    if (users.length === 0) fetchUsers();
                  }}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                    activeTab === 'users' 
                      ? 'bg-purple-500/20 text-purple-400 border border-purple-500/40' 
                      : 'text-[#6b7a99] hover:text-white hover:bg-[#111827]'
                  }`}
                  data-testid="admin-users-tab"
                >
                  <Users className="w-3 h-3 inline mr-1" />
                  Users
                </button>
                <button
                  onClick={() => {
                    setActiveTab('payments');
                    fetchPayments('pending');
                  }}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                    activeTab === 'payments' 
                      ? 'bg-green-500/20 text-green-400 border border-green-500/40' 
                      : 'text-[#6b7a99] hover:text-white hover:bg-[#111827]'
                  }`}
                  data-testid="admin-payments-tab"
                >
                  <CreditCard className="w-3 h-3 inline mr-1" />
                  Payments
                </button>
                <button
                  onClick={() => {
                    setActiveTab('settings');
                    fetchSettings();
                  }}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                    activeTab === 'settings' 
                      ? 'bg-amber-500/20 text-amber-400 border border-amber-500/40' 
                      : 'text-[#6b7a99] hover:text-white hover:bg-[#111827]'
                  }`}
                  data-testid="admin-settings-tab"
                >
                  <Settings className="w-3 h-3 inline mr-1" />
                  UPI & Plans
                </button>
                <button
                  onClick={() => {
                    setActiveTab('contacts');
                    fetchContactSettings();
                  }}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                    activeTab === 'contacts' 
                      ? 'bg-pink-500/20 text-pink-400 border border-pink-500/40' 
                      : 'text-[#6b7a99] hover:text-white hover:bg-[#111827]'
                  }`}
                  data-testid="admin-contacts-tab"
                >
                  <Phone className="w-3 h-3 inline mr-1" />
                  Contact
                </button>
                <button
                  onClick={() => {
                    setActiveTab('affiliates');
                    fetchInfluencers();
                  }}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                    activeTab === 'affiliates' 
                      ? 'bg-purple-500/20 text-purple-400 border border-purple-500/40' 
                      : 'text-[#6b7a99] hover:text-white hover:bg-[#111827]'
                  }`}
                  data-testid="admin-affiliates-tab"
                >
                  <Users className="w-3 h-3 inline mr-1" />
                  Affiliates
                </button>
                <button
                  onClick={() => {
                    setActiveTab('messages');
                    fetchBroadcasts();
                  }}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                    activeTab === 'messages' 
                      ? 'bg-teal-500/20 text-teal-400 border border-teal-500/40' 
                      : 'text-[#6b7a99] hover:text-white hover:bg-[#111827]'
                  }`}
                  data-testid="admin-messages-tab"
                >
                  <MessageSquare className="w-3 h-3 inline mr-1" />
                  Messages
                </button>
                <button
                  onClick={() => setActiveTab('permissions')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                    activeTab === 'permissions' 
                      ? 'bg-red-500/20 text-red-400 border border-red-500/40' 
                      : 'text-[#6b7a99] hover:text-white hover:bg-[#111827]'
                  }`}
                  data-testid="admin-permissions-tab"
                >
                  <Shield className="w-3 h-3 inline mr-1" />
                  Permissions
                </button>
                <button
                  onClick={() => {
                    setActiveTab('algo-setup');
                    fetchAlgoSetupConfigs();
                  }}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                    activeTab === 'algo-setup' 
                      ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/40' 
                      : 'text-[#6b7a99] hover:text-white hover:bg-[#111827]'
                  }`}
                  data-testid="admin-algo-setup-tab"
                >
                  <Table2 className="w-3 h-3 inline mr-1" />
                  Algo Setup
                </button>
                <button
                  onClick={() => setActiveTab('telegram')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all flex items-center ${
                    activeTab === 'telegram' 
                      ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/40' 
                      : 'text-[#6b7a99] hover:text-white hover:bg-[#111827]'
                  }`}
                  data-testid="admin-telegram-tab"
                >
                  <Send className="w-3 h-3 inline mr-1" />
                  Telegram
                </button>
                <button
                  onClick={() => setActiveTab('reports')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all flex items-center ${
                    activeTab === 'reports' 
                      ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/40' 
                      : 'text-[#6b7a99] hover:text-white hover:bg-[#111827]'
                  }`}
                  data-testid="admin-reports-tab"
                >
                  <FileSpreadsheet className="w-3 h-3 inline mr-1" />
                  Reports
                </button>
              </div>
              
              {/* Credentials Tab */}
              {activeTab === 'credentials' && (
                <div className="space-y-4">
                  {/* Universal Redirect URL - Prominent Display */}
                  <div className="p-4 bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/40 rounded-xl">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm text-amber-400 font-bold flex items-center gap-2">
                        <Link className="w-4 h-4" />
                        Universal Redirect URL
                      </p>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={async () => {
                          copyToClipboard(redirectUrl, 'Redirect URL copied!');
                        }}
                        className="h-7 text-[10px] border-amber-500/50 text-amber-400 hover:bg-amber-500/20"
                      >
                        <Copy className="w-3 h-3 mr-1" />
                        Copy URL
                      </Button>
                    </div>
                    <code className="text-xs text-amber-300 break-all bg-black/30 px-2 py-1 rounded block">{redirectUrl}</code>
                    <p className="text-[10px] text-amber-400/70 mt-2">
                      Use this URL in your Zerodha Kite App settings. No need to change - works for all users.
                    </p>
                  </div>

                  <div>
                    <Label className="text-[#6b7a99] text-xs">Zerodha API Key</Label>
                    <Input
                      type="text"
                      value={apiKey}
                      onChange={(e) => setApiKey(e.target.value)}
                      className="mt-1 bg-[#111827] border-[#1a2540] text-white rounded-lg text-sm"
                      placeholder="Kite API Key"
                    />
                  </div>

                  <div>
                    <Label className="text-[#6b7a99] text-xs">API Secret (leave empty to keep current)</Label>
                    <div className="relative mt-1">
                      <Input
                        type={showSecret ? "text" : "password"}
                        value={apiSecret}
                        onChange={(e) => setApiSecret(e.target.value)}
                        className="bg-[#111827] border-[#1a2540] text-white rounded-lg text-sm pr-10"
                        placeholder="Kite API Secret"
                      />
                      <button
                        onClick={() => setShowSecret(!showSecret)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-[#4a5a7a] hover:text-white"
                      >
                        {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  <div className="pt-2 border-t border-[#1a2540]">
                    <p className="text-xs text-purple-400 font-semibold mb-2">Telegram Alerts (Optional)</p>
                    <div className="space-y-2">
                      <Input
                        type="text"
                        value={telegramToken}
                        onChange={(e) => setTelegramToken(e.target.value)}
                        className="bg-[#111827] border-[#1a2540] text-white rounded-lg text-sm"
                        placeholder="Bot Token"
                      />
                      <Input
                        type="text"
                        value={telegramChatId}
                        onChange={(e) => setTelegramChatId(e.target.value)}
                        className="bg-[#111827] border-[#1a2540] text-white rounded-lg text-sm"
                        placeholder="Chat ID"
                      />
                    </div>
                    {/* Test Telegram Button */}
                    <Button
                      onClick={async () => {
                        try {
                          const response = await apiClient.post(`/admin/test-telegram?admin_password=${encodeURIComponent(password)}`);
                          if (response.data.success) {
                            toast.success(response.data.message);
                          } else {
                            toast.error(response.data.message);
                          }
                        } catch (err) {
                          toast.error('Failed to test Telegram: ' + (err.response?.data?.detail || err.message));
                        }
                      }}
                      size="sm"
                      variant="outline"
                      className="w-full mt-2 border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10"
                    >
                      <Send className="w-3 h-3 mr-2" />
                      Test Telegram Alert
                    </Button>
                  </div>

                  {/* Admin Email - For Readymade Layout Permissions */}
                  <div className="pt-2 border-t border-[#1a2540]">
                    <p className="text-xs text-cyan-400 font-semibold mb-2">Admin Login Email</p>
                    <p className="text-[10px] text-[#6b7a99] mb-2">User who logs in with this email gets admin privileges (Save Readymade Layouts, etc.)</p>
                    <div className="flex gap-2">
                      <Input
                        type="email"
                        value={adminEmail}
                        onChange={(e) => setAdminEmail(e.target.value)}
                        className="bg-[#111827] border-[#1a2540] text-white rounded-lg text-sm flex-1"
                        placeholder="admin@example.com"
                        data-testid="admin-email-input"
                      />
                      <Button
                        onClick={saveAdminEmail}
                        disabled={adminEmailLoading || !adminEmail.trim()}
                        size="sm"
                        className="bg-cyan-500 hover:bg-cyan-600 text-white"
                        data-testid="save-admin-email-btn"
                      >
                        {adminEmailLoading ? 'Saving...' : 'Save'}
                      </Button>
                    </div>
                  </div>

                  {/* Admin Panel Security */}
                  <div className="pt-2 border-t border-[#1a2540]">
                    <p className="text-xs text-amber-400 font-semibold mb-2">Admin Panel Security</p>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between p-3 bg-[#111827] border border-[#1a2540] rounded-lg">
                        <div>
                          <p className="text-sm text-white">Enable Extra Security</p>
                          <p className="text-[10px] text-[#6b7a99]">Require security code after pressing "7" seven times</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={adminSecurityEnabled}
                            onChange={(e) => {
                              setAdminSecurityEnabled(e.target.checked);
                              localStorage.setItem('admin_security_enabled', e.target.checked.toString());
                              if (!e.target.checked) {
                                localStorage.removeItem('admin_security_code');
                                setAdminSecurityCode('');
                              }
                            }}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-[#2a3a5a] peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-amber-500"></div>
                        </label>
                      </div>
                      
                      {adminSecurityEnabled && (
                        <div>
                          <Label className="text-[#6b7a99] text-xs">Security Code</Label>
                          <div className="flex gap-2 mt-1">
                            <Input
                              type="text"
                              value={adminSecurityCode}
                              onChange={(e) => setAdminSecurityCode(e.target.value)}
                              className="bg-[#111827] border-[#1a2540] text-white rounded-lg text-sm flex-1"
                              placeholder="Enter a secret code..."
                            />
                            <Button
                              onClick={() => {
                                if (adminSecurityCode.trim()) {
                                  localStorage.setItem('admin_security_code', adminSecurityCode);
                                  toast.success('Security code saved');
                                } else {
                                  toast.error('Please enter a security code');
                                }
                              }}
                              size="sm"
                              className="bg-amber-500 hover:bg-amber-600"
                            >
                              Save
                            </Button>
                          </div>
                          <p className="text-[9px] text-[#4a5a7a] mt-1">This code will be required to access admin panel</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {error && <p className="text-xs text-red-400">{error}</p>}
                  {success && <p className="text-xs text-green-400">{success}</p>}

                  <Button
                    onClick={saveCredentials}
                    disabled={isLoading}
                    className="w-full bg-gradient-to-r from-purple-500 to-pink-500 text-white font-semibold rounded-lg"
                  >
                    {isLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Save Credentials
                  </Button>

                  <Button
                    onClick={handleZerodhaLogin}
                    className="w-full bg-gradient-to-r from-[#387ED1] to-[#2563eb] text-white font-semibold rounded-lg"
                  >
                    Connect Zerodha Account
                  </Button>
                </div>
              )}
              
              {/* Users Tab */}
              {activeTab === 'users' && (
                <div className="space-y-4">
                  {/* Search and Refresh */}
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4a5a7a]" />
                      <Input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-10 bg-[#111827] border-[#1a2540] text-white rounded-lg text-sm"
                        placeholder="Search by email, name, or phone..."
                        data-testid="admin-user-search"
                      />
                    </div>
                    <Button
                      onClick={fetchUsers}
                      disabled={usersLoading}
                      variant="outline"
                      className="border-[#1a2540] text-[#6b7a99] hover:text-white"
                    >
                      <RefreshCw className={`w-4 h-4 ${usersLoading ? 'animate-spin' : ''}`} />
                    </Button>
                  </div>
                  
                  {/* Users List */}
                  <div className={`border border-[#1a2540] rounded-lg overflow-hidden overflow-y-auto ${
                    isMaximized ? 'max-h-[75vh]' : 'max-h-[50vh]'
                  }`}>
                    {usersLoading ? (
                      <div className="p-8 text-center">
                        <Loader2 className="w-6 h-6 animate-spin mx-auto text-cyan-400" />
                        <p className="text-sm text-[#6b7a99] mt-2">Loading users...</p>
                      </div>
                    ) : filteredUsers.length === 0 ? (
                      <div className="p-8 text-center">
                        <Users className="w-8 h-8 mx-auto text-[#4a5a7a]" />
                        <p className="text-sm text-[#6b7a99] mt-2">No users found</p>
                      </div>
                    ) : (
                      <table className="w-full text-sm">
                        <thead className="bg-[#111827] sticky top-0">
                          <tr>
                            <th className="text-left p-3 text-[#6b7a99] font-medium">User</th>
                            <th className="text-left p-3 text-[#6b7a99] font-medium">Subscription</th>
                            <th className="text-center p-3 text-[#6b7a99] font-medium">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredUsers.map(user => {
                            // Format dates for display
                            const formatDate = (dateStr) => {
                              if (!dateStr) return 'N/A';
                              try {
                                return new Date(dateStr).toLocaleDateString('en-IN', { 
                                  day: '2-digit', month: 'short', year: 'numeric' 
                                });
                              } catch { return 'N/A'; }
                            };
                            
                            return (
                            <tr 
                              key={user.id} 
                              className={`border-t border-[#1a2540] hover:bg-[#111827] transition-colors ${
                                selectedUser?.id === user.id ? 'bg-purple-500/10' : ''
                              } ${user.is_active === false ? 'opacity-50' : ''}`}
                            >
                              <td className="p-3">
                                <div className="flex items-center gap-2">
                                  {user.is_active === false && (
                                    <span className="w-2 h-2 bg-red-500 rounded-full" title="Deactivated" />
                                  )}
                                  {user.subscription_status === 'pending' && (
                                    <span className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse" title="Pending Approval" />
                                  )}
                                  <div>
                                    <p className="text-white font-medium">{user.name || 'N/A'}</p>
                                    <p className="text-[10px] text-[#6b7a99]">{user.email}</p>
                                    <p className="text-[9px] text-[#4a5a7a]">{user.phone || 'No phone'}</p>
                                  </div>
                                </div>
                              </td>
                              <td className="p-3">
                                <div className="flex flex-col gap-1">
                                  {/* Status Badge */}
                                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium inline-block w-fit ${
                                    user.subscription_status === 'active' 
                                      ? 'bg-green-500/20 text-green-400' 
                                      : user.subscription_status === 'expired'
                                      ? 'bg-red-500/20 text-red-400'
                                      : user.subscription_status === 'pending'
                                      ? 'bg-yellow-500/20 text-yellow-400'
                                      : 'bg-gray-500/20 text-gray-400'
                                  }`}>
                                    {user.subscription_status || 'free'}
                                  </span>
                                  
                                  {/* Subscription Dates */}
                                  {user.subscription_status === 'active' && (
                                    <div className="text-[9px] text-[#6b7a99]">
                                      <div>Start: <span className="text-cyan-400">{formatDate(user.subscription_start)}</span></div>
                                      <div>Expiry: <span className="text-orange-400">{formatDate(user.subscription_end)}</span></div>
                                      {user.days_remaining !== null && (
                                        <div className={`font-semibold ${user.days_remaining <= 7 ? 'text-red-400' : user.days_remaining <= 30 ? 'text-yellow-400' : 'text-green-400'}`}>
                                          {user.days_remaining} days left
                                        </div>
                                      )}
                                    </div>
                                  )}
                                  
                                  {user.subscription_status === 'expired' && user.subscription_end && (
                                    <div className="text-[9px] text-red-400">
                                      Expired: {formatDate(user.subscription_end)}
                                    </div>
                                  )}
                                  
                                  {user.is_active === false && (
                                    <span className="text-[9px] px-2 py-0.5 rounded-full bg-red-500/20 text-red-400 font-medium inline-block w-fit">
                                      BLOCKED
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td className="p-3">
                                <div className="flex items-center justify-center gap-1 flex-wrap">
                                  {/* Select for Password Reset / Subscription */}
                                  <Button
                                    onClick={() => {
                                      if (selectedUser?.id === user.id) {
                                        setSelectedUser(null);
                                      } else {
                                        setSelectedUser(user);
                                        setLastSetPassword(''); // Clear last password when selecting new user
                                        setNewPassword('');
                                      }
                                    }}
                                    size="sm"
                                    variant={selectedUser?.id === user.id ? "default" : "outline"}
                                    className={`text-[9px] h-6 px-2 ${
                                      selectedUser?.id === user.id 
                                        ? 'bg-purple-500 text-white' 
                                        : 'border-[#1a2540] text-[#6b7a99] hover:text-white'
                                    }`}
                                    data-testid={`select-user-${user.email}`}
                                  >
                                    <Key className="w-3 h-3 mr-1" />
                                    {selectedUser?.id === user.id ? '✓' : 'Manage'}
                                  </Button>
                                  
                                  {/* Activate/Deactivate Toggle */}
                                  {user.is_active === false ? (
                                    <Button
                                      onClick={() => handleUserAction('activate', user)}
                                      size="sm"
                                      variant="outline"
                                      disabled={actionLoading === `activate-${user.email}`}
                                      className="text-[9px] h-6 px-2 border-green-500/30 text-green-400 hover:bg-green-500/20"
                                      title="Activate User"
                                    >
                                      {actionLoading === `activate-${user.email}` ? (
                                        <Loader2 className="w-3 h-3 animate-spin" />
                                      ) : (
                                        <UserCheck className="w-3 h-3" />
                                      )}
                                    </Button>
                                  ) : (
                                    <Button
                                      onClick={() => handleUserAction('deactivate', user)}
                                      size="sm"
                                      variant="outline"
                                      disabled={actionLoading === `deactivate-${user.email}`}
                                      className="text-[9px] h-6 px-2 border-yellow-500/30 text-yellow-400 hover:bg-yellow-500/20"
                                      title="Deactivate User"
                                    >
                                      {actionLoading === `deactivate-${user.email}` ? (
                                        <Loader2 className="w-3 h-3 animate-spin" />
                                      ) : (
                                        <UserX className="w-3 h-3" />
                                      )}
                                    </Button>
                                  )}
                                  
                                  {/* Delete Button */}
                                  <Button
                                    onClick={() => setShowDeleteConfirm(user)}
                                    size="sm"
                                    variant="outline"
                                    className="text-[9px] h-6 px-2 border-red-500/30 text-red-400 hover:bg-red-500/20"
                                    title="Delete User"
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          );
                          })}
                        </tbody>
                      </table>
                    )}
                  </div>
                  
                  {/* Delete Confirmation Modal */}
                  {showDeleteConfirm && (
                    <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg space-y-3">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-semibold text-red-400">⚠️ Delete User</p>
                        <button 
                          onClick={() => setShowDeleteConfirm(null)}
                          className="text-[#6b7a99] hover:text-white"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                      <p className="text-xs text-[#6b7a99]">
                        Are you sure you want to permanently delete: <span className="text-white font-medium">{showDeleteConfirm.email}</span>?
                      </p>
                      <p className="text-[10px] text-red-400">This action cannot be undone!</p>
                      <div className="flex gap-2">
                        <Button
                          onClick={() => setShowDeleteConfirm(null)}
                          variant="outline"
                          className="flex-1 border-[#1a2540] text-[#6b7a99] hover:text-white"
                        >
                          Cancel
                        </Button>
                        <Button
                          onClick={() => handleUserAction('delete', showDeleteConfirm)}
                          disabled={actionLoading === `delete-${showDeleteConfirm.email}`}
                          className="flex-1 bg-red-500 hover:bg-red-600 text-white"
                        >
                          {actionLoading === `delete-${showDeleteConfirm.email}` ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4 mr-2" />
                          )}
                          Delete
                        </Button>
                      </div>
                    </div>
                  )}
                  
                  {/* User Management Section - Password Reset & Subscription */}
                  {selectedUser && !showDeleteConfirm && (
                    <div className="p-4 bg-purple-500/10 border border-purple-500/30 rounded-lg space-y-4">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-semibold text-purple-400">Manage User</p>
                        <button 
                          onClick={() => setSelectedUser(null)}
                          className="text-[#6b7a99] hover:text-white"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                      <p className="text-xs text-[#6b7a99]">
                        Managing: <span className="text-white font-medium">{selectedUser.email}</span>
                        {selectedUser.name && <span className="text-[#4a5a7a]"> ({selectedUser.name})</span>}
                      </p>
                      
                      {/* Password Reset */}
                      <div className="space-y-2 pt-2 border-t border-[#1a2540]">
                        <div className="flex items-center justify-between">
                          <p className="text-xs text-cyan-400 font-medium">Reset Password</p>
                          <Button
                            onClick={generatePassword}
                            size="sm"
                            variant="outline"
                            className="text-[9px] h-6 px-2 border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/20"
                          >
                            <Shuffle className="w-3 h-3 mr-1" />
                            Generate
                          </Button>
                        </div>
                        <div className="relative">
                          <Input
                            type={showNewPassword ? "text" : "password"}
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            className="bg-[#111827] border-[#1a2540] text-white rounded-lg text-sm pr-20"
                            placeholder="Enter new password (min 6 characters)"
                            data-testid="admin-new-password"
                          />
                          <div className="absolute right-2 top-1/2 -translate-y-1/2 flex gap-1">
                            {newPassword && (
                              <button
                                onClick={() => copyPassword(newPassword)}
                                className="text-[#4a5a7a] hover:text-cyan-400 p-1"
                                title="Copy password"
                              >
                                <Copy className="w-4 h-4" />
                              </button>
                            )}
                            <button
                              onClick={() => setShowNewPassword(!showNewPassword)}
                              className="text-[#4a5a7a] hover:text-white p-1"
                              title={showNewPassword ? "Hide password" : "Show password"}
                            >
                              {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </button>
                          </div>
                        </div>
                        
                        {/* Show password in large text when visible */}
                        {showNewPassword && newPassword && (
                          <div className="p-2 bg-cyan-500/10 border border-cyan-500/30 rounded-lg">
                            <p className="text-[10px] text-cyan-400 mb-1">Password Preview:</p>
                            <div className="flex items-center justify-between">
                              <code className="text-lg font-mono text-white tracking-wider">{newPassword}</code>
                              <Button
                                onClick={() => copyPassword(newPassword)}
                                size="sm"
                                variant="ghost"
                                className="h-7 text-cyan-400 hover:text-cyan-300"
                              >
                                <Copy className="w-4 h-4 mr-1" />
                                Copy
                              </Button>
                            </div>
                          </div>
                        )}
                        
                        {/* Show last set password */}
                        {lastSetPassword && selectedUser && (
                          <div className="p-2 bg-green-500/10 border border-green-500/30 rounded-lg">
                            <p className="text-[10px] text-green-400 mb-1">✓ Last Set Password:</p>
                            <div className="flex items-center justify-between">
                              <code className="text-lg font-mono text-white tracking-wider">{lastSetPassword}</code>
                              <Button
                                onClick={() => copyPassword(lastSetPassword)}
                                size="sm"
                                variant="ghost"
                                className="h-7 text-green-400 hover:text-green-300"
                              >
                                <Copy className="w-4 h-4 mr-1" />
                                Copy
                              </Button>
                            </div>
                          </div>
                        )}
                        
                        <Button
                          onClick={handleResetPassword}
                          disabled={resetLoading || newPassword.length < 6}
                          className="w-full bg-gradient-to-r from-cyan-500 to-blue-500 text-white font-semibold rounded-lg text-sm"
                          data-testid="admin-reset-password-btn"
                        >
                          {resetLoading ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <RefreshCw className="w-4 h-4 mr-2" />
                          )}
                          Reset Password
                        </Button>
                      </div>
                      
                      {/* Grant Subscription */}
                      <div className="space-y-2 pt-2 border-t border-[#1a2540]">
                        <p className="text-xs text-green-400 font-medium">Grant Subscription</p>
                        <div className="flex gap-2">
                          <div className="flex-1">
                            <Label className="text-[10px] text-[#6b7a99]">Days</Label>
                            <Input
                              type="number"
                              value={subscriptionDays}
                              onChange={(e) => setSubscriptionDays(parseInt(e.target.value) || 0)}
                              min={1}
                              max={365}
                              className="bg-[#111827] border-[#1a2540] text-white rounded-lg text-sm"
                              placeholder="Days"
                            />
                          </div>
                          <div className="flex items-end gap-1">
                            <Button
                              onClick={() => setSubscriptionDays(30)}
                              size="sm"
                              variant="outline"
                              className="text-[9px] h-9 border-[#1a2540] text-[#6b7a99] hover:text-white"
                            >
                              30d
                            </Button>
                            <Button
                              onClick={() => setSubscriptionDays(90)}
                              size="sm"
                              variant="outline"
                              className="text-[9px] h-9 border-[#1a2540] text-[#6b7a99] hover:text-white"
                            >
                              90d
                            </Button>
                            <Button
                              onClick={() => setSubscriptionDays(365)}
                              size="sm"
                              variant="outline"
                              className="text-[9px] h-9 border-[#1a2540] text-[#6b7a99] hover:text-white"
                            >
                              1yr
                            </Button>
                          </div>
                        </div>
                        <Button
                          onClick={() => handleGrantSubscription(selectedUser)}
                          disabled={actionLoading === `subscription-${selectedUser.email}` || subscriptionDays < 1}
                          className="w-full bg-gradient-to-r from-green-500 to-emerald-500 text-white font-semibold rounded-lg text-sm"
                        >
                          {actionLoading === `subscription-${selectedUser.email}` ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <Calendar className="w-4 h-4 mr-2" />
                          )}
                          Grant {subscriptionDays} Days Access
                        </Button>
                      </div>
                    </div>
                  )}
                  
                  <p className="text-[10px] text-[#4a5a7a] text-center">
                    Total Users: {users.length} | Showing: {filteredUsers.length}
                  </p>
                </div>
              )}
              
              {/* Payments Tab */}
              {activeTab === 'payments' && (
                <div className="space-y-4">
                  {/* Filter Buttons */}
                  <div className="flex gap-2">
                    {['pending', 'approved', 'rejected', 'all'].map((filter) => (
                      <button
                        key={filter}
                        onClick={() => {
                          setPaymentFilter(filter);
                          fetchPayments(filter);
                        }}
                        className={`px-3 py-1 rounded text-xs font-medium transition-all ${
                          paymentFilter === filter
                            ? filter === 'pending' ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/40' :
                              filter === 'approved' ? 'bg-green-500/20 text-green-400 border border-green-500/40' :
                              filter === 'rejected' ? 'bg-red-500/20 text-red-400 border border-red-500/40' :
                              'bg-blue-500/20 text-blue-400 border border-blue-500/40'
                            : 'text-[#6b7a99] hover:text-white border border-transparent'
                        }`}
                      >
                        {filter.charAt(0).toUpperCase() + filter.slice(1)}
                      </button>
                    ))}
                  </div>
                  
                  {paymentsLoading ? (
                    <div className="flex justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-cyan-400" />
                    </div>
                  ) : pendingPayments.length === 0 ? (
                    <div className="text-center py-8 text-[#6b7a99]">
                      <CreditCard className="w-10 h-10 mx-auto mb-2 opacity-50" />
                      <p>No {paymentFilter} payments</p>
                    </div>
                  ) : (
                    <div className="space-y-3 max-h-[50vh] overflow-y-auto">
                      {pendingPayments.map((payment) => (
                        <div key={payment.payment_id} className="p-3 bg-[#111827] border border-[#1a2540] rounded-lg">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-semibold text-white">{payment.user_name || payment.user_email}</span>
                                <span className={`px-2 py-0.5 text-[9px] rounded-full ${
                                  payment.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                                  payment.status === 'approved' ? 'bg-green-500/20 text-green-400' :
                                  'bg-red-500/20 text-red-400'
                                }`}>
                                  {payment.status}
                                </span>
                              </div>
                              <p className="text-xs text-[#6b7a99]">{payment.user_email}</p>
                              <div className="mt-2 flex items-center gap-4 text-xs">
                                <span className="text-cyan-400">{payment.plan_name}</span>
                                <span className="text-green-400 font-semibold">₹{payment.amount?.toLocaleString()}</span>
                                <span className="text-[#4a5a7a]">{payment.duration_days} days</span>
                              </div>
                              {payment.utr_number && (
                                <p className="mt-1 text-xs font-mono text-amber-400">UTR: {payment.utr_number}</p>
                              )}
                              {payment.screenshot_url && (
                                <div className="mt-2">
                                  <div className="relative group inline-block">
                                    <img 
                                      src={payment.screenshot_url.startsWith('/api') 
                                        ? `${process.env.REACT_APP_BACKEND_URL}${payment.screenshot_url}` 
                                        : payment.screenshot_url}
                                      alt="Payment Screenshot"
                                      className="w-16 h-16 object-cover rounded border border-[#1a2540] cursor-pointer hover:opacity-80"
                                      onClick={() => {
                                        const imgUrl = payment.screenshot_url.startsWith('/api') 
                                          ? `${process.env.REACT_APP_BACKEND_URL}${payment.screenshot_url}` 
                                          : payment.screenshot_url;
                                        window.open(imgUrl, '_blank');
                                      }}
                                    />
                                    <div className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover:opacity-100 rounded pointer-events-none">
                                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                                      </svg>
                                    </div>
                                  </div>
                                  <p className="text-[9px] text-[#4a5a7a] mt-0.5">Click to magnify</p>
                                </div>
                              )}
                              <p className="mt-1 text-[10px] text-[#4a5a7a]">
                                ID: {payment.payment_id} | {new Date(payment.submitted_at).toLocaleString()}
                              </p>
                            </div>
                            
                            {payment.status === 'pending' && (
                              <div className="flex gap-2">
                                <Button
                                  onClick={() => handlePaymentAction(payment.payment_id, 'approve')}
                                  disabled={actionLoading === `payment-${payment.payment_id}`}
                                  size="sm"
                                  className="bg-green-600 hover:bg-green-700 text-white text-xs"
                                >
                                  {actionLoading === `payment-${payment.payment_id}` ? (
                                    <Loader2 className="w-3 h-3 animate-spin" />
                                  ) : (
                                    <CheckCircle className="w-3 h-3" />
                                  )}
                                </Button>
                                <Button
                                  onClick={() => handlePaymentAction(payment.payment_id, 'reject', 'Payment could not be verified')}
                                  disabled={actionLoading === `payment-${payment.payment_id}`}
                                  size="sm"
                                  variant="destructive"
                                  className="text-xs"
                                >
                                  <XCircle className="w-3 h-3" />
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  <p className="text-[10px] text-[#4a5a7a] text-center">
                    Showing {pendingPayments.length} {paymentFilter} payment(s)
                  </p>
                </div>
              )}
              
              {/* UPI & Plans Settings Tab */}
              {activeTab === 'settings' && (
                <div className="space-y-4">
                  {settingsLoading ? (
                    <div className="flex justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-cyan-400" />
                    </div>
                  ) : (
                    <>
                      {/* UPI Settings */}
                      <div className="p-4 bg-[#111827] border border-[#1a2540] rounded-lg">
                        <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                          <QrCode className="w-4 h-4 text-cyan-400" />
                          UPI Payment Settings
                        </h3>
                        <div className="space-y-3">
                          <div>
                            <Label className="text-[#6b7a99] text-xs">UPI ID</Label>
                            <Input
                              type="text"
                              value={upiId}
                              onChange={(e) => setUpiId(e.target.value)}
                              className="mt-1 bg-[#0a0e17] border-[#1a2540] text-white rounded-lg text-sm"
                              placeholder="yourname@ybl or 9876543210@ybl"
                              data-testid="upi-id-input"
                            />
                          </div>
                          
                          {/* QR Code Upload Section */}
                          <div>
                            <Label className="text-[#6b7a99] text-xs">QR Code Image</Label>
                            <div className="mt-1 flex gap-2">
                              <Input
                                type="url"
                                value={qrCodeUrl}
                                onChange={(e) => setQrCodeUrl(e.target.value)}
                                className="flex-1 bg-[#0a0e17] border-[#1a2540] text-white rounded-lg text-sm"
                                placeholder="URL will appear after upload"
                                data-testid="qr-code-url-input"
                              />
                              <label className="cursor-pointer">
                                <input
                                  type="file"
                                  accept="image/png,image/jpeg,image/webp"
                                  className="hidden"
                                  onChange={async (e) => {
                                    const file = e.target.files?.[0];
                                    if (!file) return;
                                    
                                    if (file.size > 5 * 1024 * 1024) {
                                      toast.error('File size must be less than 5MB');
                                      return;
                                    }
                                    
                                    const formData = new FormData();
                                    formData.append('file', file);
                                    
                                    try {
                                      toast.loading('Uploading QR code...', { id: 'qr-upload' });
                                      const response = await apiClient.post(
                                        `/upload/qr-code?admin_password=${encodeURIComponent(password)}`,
                                        formData,
                                        { headers: { 'Content-Type': 'multipart/form-data' } }
                                      );
                                      
                                      if (response.data.success) {
                                        setQrCodeUrl(response.data.url);
                                        toast.success('QR code uploaded!', { id: 'qr-upload' });
                                      }
                                    } catch (err) {
                                      toast.error(err.response?.data?.detail || 'Upload failed', { id: 'qr-upload' });
                                    }
                                    e.target.value = '';
                                  }}
                                />
                                <div className="px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-600 text-white text-sm font-medium rounded-lg hover:from-green-600 hover:to-emerald-700 flex items-center gap-2">
                                  <Upload className="w-4 h-4" />
                                  Upload
                                </div>
                              </label>
                            </div>
                            <p className="text-[9px] text-[#4a5a7a] mt-1">Upload your PhonePe/GPay/Paytm QR code (PNG, JPEG, max 5MB)</p>
                          </div>
                          
                          {/* QR Preview */}
                          {qrCodeUrl && (
                            <div className="flex flex-col items-center gap-2 p-3 bg-[#0a0e17] rounded-lg">
                              <img src={qrCodeUrl} alt="QR Preview" className="w-32 h-32 object-contain rounded bg-white p-2" />
                              <p className="text-[9px] text-green-400">QR Code Preview</p>
                            </div>
                          )}
                          
                          <div>
                            <Label className="text-[#6b7a99] text-xs">Payment Instructions</Label>
                            <Input
                              type="text"
                              value={paymentInstructions}
                              onChange={(e) => setPaymentInstructions(e.target.value)}
                              className="mt-1 bg-[#0a0e17] border-[#1a2540] text-white rounded-lg text-sm"
                              placeholder="Instructions for users..."
                            />
                          </div>
                        </div>
                      </div>
                      
                      {/* Bank Details Section */}
                      <div className="p-4 bg-[#111827] border border-[#1a2540] rounded-lg">
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                            <CreditCard className="w-4 h-4 text-blue-400" />
                            Bank Details (Optional)
                          </h3>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <span className="text-[10px] text-[#6b7a99]">Show to users</span>
                            <input
                              type="checkbox"
                              checked={showBankDetailsAdmin}
                              onChange={(e) => setShowBankDetailsAdmin(e.target.checked)}
                              className="w-4 h-4 rounded border-[#1a2540] bg-[#0a0e17] text-cyan-500"
                            />
                          </label>
                        </div>
                        <div className="space-y-3">
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <Label className="text-[#6b7a99] text-xs">Bank Name</Label>
                              <Input
                                type="text"
                                value={bankName}
                                onChange={(e) => setBankName(e.target.value)}
                                className="mt-1 bg-[#0a0e17] border-[#1a2540] text-white rounded-lg text-sm"
                                placeholder="HDFC Bank"
                              />
                            </div>
                            <div>
                              <Label className="text-[#6b7a99] text-xs">Account Holder Name</Label>
                              <Input
                                type="text"
                                value={accountHolder}
                                onChange={(e) => setAccountHolder(e.target.value)}
                                className="mt-1 bg-[#0a0e17] border-[#1a2540] text-white rounded-lg text-sm"
                                placeholder="John Doe"
                              />
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <Label className="text-[#6b7a99] text-xs">Account Number</Label>
                              <Input
                                type="text"
                                value={accountNumber}
                                onChange={(e) => setAccountNumber(e.target.value)}
                                className="mt-1 bg-[#0a0e17] border-[#1a2540] text-white rounded-lg text-sm font-mono"
                                placeholder="1234567890"
                              />
                            </div>
                            <div>
                              <Label className="text-[#6b7a99] text-xs">IFSC Code</Label>
                              <Input
                                type="text"
                                value={ifscCode}
                                onChange={(e) => setIfscCode(e.target.value.toUpperCase())}
                                className="mt-1 bg-[#0a0e17] border-[#1a2540] text-white rounded-lg text-sm font-mono"
                                placeholder="HDFC0001234"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Zerodha Setup Tutorial Video */}
                      <div className="p-4 bg-[#111827] border border-[#1a2540] rounded-lg">
                        <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                          <PlayCircle className="w-4 h-4 text-red-500" />
                          Zerodha Setup Tutorial Video
                        </h3>
                        <div>
                          <Label className="text-[#6b7a99] text-xs">YouTube Video Link</Label>
                          <Input
                            type="url"
                            value={zerodhaSetupVideo}
                            onChange={(e) => setZerodhaSetupVideo(e.target.value)}
                            className="mt-1 bg-[#0a0e17] border-[#1a2540] text-white rounded-lg text-sm"
                            placeholder="https://www.youtube.com/watch?v=xxxxx"
                          />
                          <p className="text-[10px] text-[#6b7a99] mt-1">
                            This video will be shown on the "Connect Broker" page to help users set up their Zerodha account.
                          </p>
                        </div>
                      </div>
                      
                      {/* Save All Settings Button */}
                      <Button
                        onClick={saveUpiSettings}
                        disabled={settingsLoading}
                        className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-semibold"
                      >
                        <Save className="w-4 h-4 mr-2" />
                        Save All Payment Settings
                      </Button>
                      
                      {/* Subscription Plans */}
                      <div className="p-4 bg-[#111827] border border-[#1a2540] rounded-lg">
                        <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                          <Crown className="w-4 h-4 text-amber-400" />
                          Subscription Plans
                        </h3>
                        <div className="space-y-2">
                          {Object.entries(subscriptionPlans).sort((a, b) => a[1].duration_days - b[1].duration_days).map(([planId, plan]) => (
                            <div key={planId} className="p-3 bg-[#0a0e17] border border-[#1a2540] rounded-lg">
                              {editingPlan === planId ? (
                                <div className="space-y-2">
                                  <div className="grid grid-cols-3 gap-2">
                                    <Input
                                      type="text"
                                      defaultValue={plan.name}
                                      id={`plan-name-${planId}`}
                                      className="bg-[#111827] border-[#1a2540] text-white text-xs"
                                      placeholder="Plan Name"
                                    />
                                    <Input
                                      type="number"
                                      defaultValue={plan.duration_days}
                                      id={`plan-days-${planId}`}
                                      className="bg-[#111827] border-[#1a2540] text-white text-xs"
                                      placeholder="Days"
                                    />
                                    <Input
                                      type="number"
                                      defaultValue={plan.price}
                                      id={`plan-price-${planId}`}
                                      className="bg-[#111827] border-[#1a2540] text-white text-xs"
                                      placeholder="Price"
                                    />
                                  </div>
                                  <div className="flex gap-2">
                                    <Button
                                      onClick={() => {
                                        const name = document.getElementById(`plan-name-${planId}`).value;
                                        const days = parseInt(document.getElementById(`plan-days-${planId}`).value);
                                        const price = parseInt(document.getElementById(`plan-price-${planId}`).value);
                                        savePlan(planId, { name, duration_days: days, price });
                                      }}
                                      size="sm"
                                      className="bg-green-600 text-white text-xs"
                                    >
                                      Save
                                    </Button>
                                    <Button
                                      onClick={() => setEditingPlan(null)}
                                      size="sm"
                                      variant="outline"
                                      className="text-xs border-[#1a2540]"
                                    >
                                      Cancel
                                    </Button>
                                    <Button
                                      onClick={() => savePlan(planId, { active: !plan.active })}
                                      size="sm"
                                      variant="outline"
                                      className={`text-xs ${plan.active ? 'text-red-400 border-red-500/40' : 'text-green-400 border-green-500/40'}`}
                                    >
                                      {plan.active ? 'Disable' : 'Enable'}
                                    </Button>
                                  </div>
                                </div>
                              ) : (
                                <div className="flex items-center justify-between">
                                  <div>
                                    <span className={`text-sm font-semibold ${plan.active ? 'text-white' : 'text-[#4a5a7a] line-through'}`}>
                                      {plan.name}
                                    </span>
                                    <span className="ml-2 text-xs text-[#6b7a99]">({plan.duration_days} days)</span>
                                    {!plan.active && <span className="ml-2 text-[9px] text-red-400">DISABLED</span>}
                                  </div>
                                  <div className="flex items-center gap-3">
                                    <span className="text-green-400 font-bold">₹{plan.price?.toLocaleString()}</span>
                                    <button
                                      onClick={() => setEditingPlan(planId)}
                                      className="text-cyan-400 hover:text-cyan-300 text-xs"
                                    >
                                      Edit
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}
              
              {/* Contacts Tab */}
              {activeTab === 'contacts' && (
                <div className="space-y-4">
                  {settingsLoading ? (
                    <div className="flex justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-cyan-400" />
                    </div>
                  ) : (
                    <>
                      {/* Enable/Disable Contacts */}
                      <div className="flex items-center justify-between p-3 bg-[#111827] border border-[#1a2540] rounded-lg">
                        <div className="flex items-center gap-2">
                          <Phone className="w-4 h-4 text-pink-400" />
                          <span className="text-sm font-medium text-white">Show Contact Icons</span>
                        </div>
                        <label className="flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={showContactsEnabled}
                            onChange={(e) => setShowContactsEnabled(e.target.checked)}
                            className="w-5 h-5 rounded border-[#1a2540] bg-[#0a0e17] text-pink-500"
                          />
                        </label>
                      </div>
                      
                      {/* WhatsApp Settings */}
                      <div className="p-4 bg-[#111827] border border-[#1a2540] rounded-lg">
                        <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                          <MessageCircle className="w-4 h-4 text-green-400" />
                          WhatsApp
                        </h3>
                        <div className="space-y-3">
                          <div>
                            <Label className="text-[#6b7a99] text-xs">WhatsApp Number (with country code)</Label>
                            <Input
                              type="text"
                              value={whatsappNumber}
                              onChange={(e) => setWhatsappNumber(e.target.value)}
                              className="mt-1 bg-[#0a0e17] border-[#1a2540] text-white rounded-lg text-sm"
                              placeholder="919876543210"
                            />
                          </div>
                          <div>
                            <Label className="text-[#6b7a99] text-xs">Default Message (Pre-filled)</Label>
                            <Input
                              type="text"
                              value={whatsappMessage}
                              onChange={(e) => setWhatsappMessage(e.target.value)}
                              className="mt-1 bg-[#0a0e17] border-[#1a2540] text-white rounded-lg text-sm"
                              placeholder="Hello! I'm interested in..."
                            />
                          </div>
                        </div>
                      </div>
                      
                      {/* Phone Settings */}
                      <div className="p-4 bg-[#111827] border border-[#1a2540] rounded-lg">
                        <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                          <Phone className="w-4 h-4 text-blue-400" />
                          Phone Call
                        </h3>
                        <div className="space-y-3">
                          <div>
                            <Label className="text-[#6b7a99] text-xs">Phone Number</Label>
                            <Input
                              type="text"
                              value={phoneNumberContact}
                              onChange={(e) => setPhoneNumberContact(e.target.value)}
                              className="mt-1 bg-[#0a0e17] border-[#1a2540] text-white rounded-lg text-sm"
                              placeholder="+919876543210"
                            />
                          </div>
                          <div>
                            <Label className="text-[#6b7a99] text-xs">Busy/No Answer Message</Label>
                            <Input
                              type="text"
                              value={phoneBusyMessage}
                              onChange={(e) => setPhoneBusyMessage(e.target.value)}
                              className="mt-1 bg-[#0a0e17] border-[#1a2540] text-white rounded-lg text-sm"
                              placeholder="Admin will contact you shortly..."
                            />
                          </div>
                          <div>
                            <Label className="text-[#6b7a99] text-xs">Email Address</Label>
                            <Input
                              type="email"
                              value={emailAddress}
                              onChange={(e) => setEmailAddress(e.target.value)}
                              className="mt-1 bg-[#0a0e17] border-[#1a2540] text-white rounded-lg text-sm"
                              placeholder="contact@yourcompany.com"
                            />
                          </div>
                        </div>
                      </div>
                      
                      {/* Social Links */}
                      <div className="p-4 bg-[#111827] border border-[#1a2540] rounded-lg">
                        <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                          <Send className="w-4 h-4 text-sky-400" />
                          Social Links
                        </h3>
                        <div className="space-y-3">
                          <div>
                            <Label className="text-[#6b7a99] text-xs">Telegram Group/Channel Link</Label>
                            <Input
                              type="url"
                              value={telegramGroup}
                              onChange={(e) => setTelegramGroup(e.target.value)}
                              className="mt-1 bg-[#0a0e17] border-[#1a2540] text-white rounded-lg text-sm"
                              placeholder="https://t.me/yourchannel"
                            />
                          </div>
                          <div>
                            <Label className="text-[#6b7a99] text-xs">YouTube Channel Link</Label>
                            <Input
                              type="url"
                              value={youtubeChannel}
                              onChange={(e) => setYoutubeChannel(e.target.value)}
                              className="mt-1 bg-[#0a0e17] border-[#1a2540] text-white rounded-lg text-sm"
                              placeholder="https://youtube.com/@yourchannel"
                            />
                          </div>
                          <div>
                            <Label className="text-[#6b7a99] text-xs">Facebook Page Link</Label>
                            <Input
                              type="url"
                              value={facebookPage}
                              onChange={(e) => setFacebookPage(e.target.value)}
                              className="mt-1 bg-[#0a0e17] border-[#1a2540] text-white rounded-lg text-sm"
                              placeholder="https://facebook.com/yourpage"
                            />
                          </div>
                          <div>
                            <Label className="text-[#6b7a99] text-xs">LinkedIn Profile Link</Label>
                            <Input
                              type="url"
                              value={linkedinProfile}
                              onChange={(e) => setLinkedinProfile(e.target.value)}
                              className="mt-1 bg-[#0a0e17] border-[#1a2540] text-white rounded-lg text-sm"
                              placeholder="https://linkedin.com/in/yourprofile"
                            />
                          </div>
                          <div>
                            <Label className="text-[#6b7a99] text-xs">Instagram Profile Link</Label>
                            <Input
                              type="url"
                              value={instagramProfile}
                              onChange={(e) => setInstagramProfile(e.target.value)}
                              className="mt-1 bg-[#0a0e17] border-[#1a2540] text-white rounded-lg text-sm"
                              placeholder="https://instagram.com/yourprofile"
                              data-testid="admin-instagram-input"
                            />
                          </div>
                        </div>
                      </div>
                      
                      {/* Welcome Message */}
                      <div className="p-4 bg-[#111827] border border-[#1a2540] rounded-lg">
                        <h3 className="text-sm font-semibold text-white mb-3">Welcome Message</h3>
                        <Input
                          type="text"
                          value={welcomeMessage}
                          onChange={(e) => setWelcomeMessage(e.target.value)}
                          className="bg-[#0a0e17] border-[#1a2540] text-white rounded-lg text-sm"
                          placeholder="Welcome to AlphaTradeVission!"
                        />
                      </div>
                      
                      {/* Save Button */}
                      <Button
                        onClick={saveContactSettings}
                        disabled={settingsLoading}
                        className="w-full bg-gradient-to-r from-pink-500 to-rose-600 text-white font-semibold"
                      >
                        <Save className="w-4 h-4 mr-2" />
                        Save Contact Settings
                      </Button>
                    </>
                  )}
                </div>
              )}
              
              {/* Affiliates/Influencers Tab */}
              {activeTab === 'affiliates' && (
                <div className="space-y-4">
                  {influencersLoading ? (
                    <div className="flex justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-purple-400" />
                    </div>
                  ) : (
                    <>
                      {/* Add New Influencer Button */}
                      <div className="flex justify-between items-center">
                        <h3 className="text-sm font-semibold text-white">Influencers & Affiliates</h3>
                        <Button
                          onClick={() => setShowAddInfluencer(!showAddInfluencer)}
                          size="sm"
                          className="bg-purple-600 hover:bg-purple-700 text-white text-xs"
                        >
                          {showAddInfluencer ? 'Cancel' : '+ Add New'}
                        </Button>
                      </div>
                      
                      {/* Add New Influencer Form */}
                      {showAddInfluencer && (
                        <div className="p-3 bg-purple-500/10 border border-purple-500/30 rounded-lg space-y-3">
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <Label className="text-[#6b7a99] text-xs">Name *</Label>
                              <Input
                                type="text"
                                value={newInfluencer.name}
                                onChange={(e) => setNewInfluencer({...newInfluencer, name: e.target.value})}
                                className="mt-1 bg-[#0a0e17] border-[#1a2540] text-white rounded-lg text-sm"
                                placeholder="Influencer Name"
                              />
                            </div>
                            <div>
                              <Label className="text-[#6b7a99] text-xs">Promo Code *</Label>
                              <Input
                                type="text"
                                value={newInfluencer.promo_code}
                                onChange={(e) => setNewInfluencer({...newInfluencer, promo_code: e.target.value.toUpperCase()})}
                                className="mt-1 bg-[#0a0e17] border-[#1a2540] text-white rounded-lg text-sm font-mono"
                                placeholder="PROMOCODE"
                              />
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <Label className="text-[#6b7a99] text-xs">Email</Label>
                              <Input
                                type="email"
                                value={newInfluencer.email || ''}
                                onChange={(e) => setNewInfluencer({...newInfluencer, email: e.target.value})}
                                className="mt-1 bg-[#0a0e17] border-[#1a2540] text-white rounded-lg text-sm"
                                placeholder="influencer@email.com"
                              />
                            </div>
                            <div>
                              <Label className="text-[#6b7a99] text-xs">Phone (with country code)</Label>
                              <Input
                                type="text"
                                value={newInfluencer.phone || ''}
                                onChange={(e) => setNewInfluencer({...newInfluencer, phone: e.target.value})}
                                className="mt-1 bg-[#0a0e17] border-[#1a2540] text-white rounded-lg text-sm"
                                placeholder="+91 9876543210"
                              />
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <Label className="text-[#6b7a99] text-xs">Commission % (10-30)</Label>
                              <Input
                                type="number"
                                min="10"
                                max="30"
                                value={newInfluencer.commission_percent}
                                onChange={(e) => setNewInfluencer({...newInfluencer, commission_percent: parseInt(e.target.value) || 10})}
                                className="mt-1 bg-[#0a0e17] border-[#1a2540] text-white rounded-lg text-sm"
                              />
                            </div>
                            <div className="flex items-center mt-5">
                              <input
                                type="checkbox"
                                id="lifetime-partner"
                                checked={newInfluencer.is_lifetime_partner}
                                onChange={(e) => setNewInfluencer({...newInfluencer, is_lifetime_partner: e.target.checked})}
                                className="mr-2"
                              />
                              <Label htmlFor="lifetime-partner" className="text-[#6b7a99] text-xs cursor-pointer">
                                Lifetime Partner (earns on all renewals)
                              </Label>
                            </div>
                          </div>
                          <Button
                            onClick={createInfluencer}
                            className="w-full bg-gradient-to-r from-purple-500 to-violet-600 text-white text-xs"
                          >
                            Create Influencer
                          </Button>
                        </div>
                      )}
                      
                      {/* Influencers List */}
                      <div className="space-y-2">
                        {influencers.length === 0 ? (
                          <p className="text-center text-[#6b7a99] text-xs py-4">No influencers added yet</p>
                        ) : (
                          influencers.map((inf) => (
                            <div key={inf.influencer_id} className={`p-3 rounded-lg border ${inf.is_active ? 'bg-[#111827] border-[#1a2540]' : 'bg-red-900/10 border-red-500/30'}`}>
                              {editingInfluencer === inf.influencer_id ? (
                                <div className="space-y-2">
                                  <div className="grid grid-cols-2 gap-2">
                                    <Input
                                      type="text"
                                      value={inf.name}
                                      onChange={(e) => setInfluencers(influencers.map(i => 
                                        i.influencer_id === inf.influencer_id ? {...i, name: e.target.value} : i
                                      ))}
                                      className="bg-[#0a0e17] border-[#1a2540] text-white rounded-lg text-xs"
                                      placeholder="Name"
                                    />
                                    <Input
                                      type="number"
                                      min="10"
                                      max="30"
                                      value={inf.commission_percent}
                                      onChange={(e) => setInfluencers(influencers.map(i => 
                                        i.influencer_id === inf.influencer_id ? {...i, commission_percent: parseInt(e.target.value)} : i
                                      ))}
                                      className="bg-[#0a0e17] border-[#1a2540] text-white rounded-lg text-xs"
                                      placeholder="Commission %"
                                    />
                                  </div>
                                  <div className="grid grid-cols-2 gap-2">
                                    <Input
                                      type="email"
                                      value={inf.email || ''}
                                      onChange={(e) => setInfluencers(influencers.map(i => 
                                        i.influencer_id === inf.influencer_id ? {...i, email: e.target.value} : i
                                      ))}
                                      className="bg-[#0a0e17] border-[#1a2540] text-white rounded-lg text-xs"
                                      placeholder="Email"
                                    />
                                    <Input
                                      type="text"
                                      value={inf.phone || ''}
                                      onChange={(e) => setInfluencers(influencers.map(i => 
                                        i.influencer_id === inf.influencer_id ? {...i, phone: e.target.value} : i
                                      ))}
                                      className="bg-[#0a0e17] border-[#1a2540] text-white rounded-lg text-xs"
                                      placeholder="Phone (+91...)"
                                    />
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <input
                                      type="checkbox"
                                      checked={inf.is_lifetime_partner}
                                      onChange={(e) => setInfluencers(influencers.map(i => 
                                        i.influencer_id === inf.influencer_id ? {...i, is_lifetime_partner: e.target.checked} : i
                                      ))}
                                    />
                                    <span className="text-xs text-[#6b7a99]">Lifetime Partner</span>
                                    <input
                                      type="checkbox"
                                      checked={inf.is_active}
                                      onChange={(e) => setInfluencers(influencers.map(i => 
                                        i.influencer_id === inf.influencer_id ? {...i, is_active: e.target.checked} : i
                                      ))}
                                    />
                                    <span className="text-xs text-[#6b7a99]">Active</span>
                                  </div>
                                  <div className="flex gap-2">
                                    <Button size="sm" onClick={() => updateInfluencer(inf)} className="bg-green-600 text-xs">Save</Button>
                                    <Button size="sm" variant="outline" onClick={() => setEditingInfluencer(null)} className="text-xs">Cancel</Button>
                                  </div>
                                </div>
                              ) : (
                                <>
                                  <div className="flex justify-between items-start">
                                    <div>
                                      <div className="flex items-center gap-2">
                                        <span className="font-semibold text-white text-sm">{inf.name}</span>
                                        {inf.is_lifetime_partner && (
                                          <span className="text-[9px] bg-amber-500/20 text-amber-400 px-1.5 py-0.5 rounded">PARTNER</span>
                                        )}
                                        {!inf.is_active && (
                                          <span className="text-[9px] bg-red-500/20 text-red-400 px-1.5 py-0.5 rounded">INACTIVE</span>
                                        )}
                                      </div>
                                      <p className="text-xs text-cyan-400 font-mono">{inf.promo_code}</p>
                                      {(inf.email || inf.phone) && (
                                        <div className="mt-1 text-[10px] text-[#6b7a99]">
                                          {inf.email && <span className="mr-2">📧 {inf.email}</span>}
                                          {inf.phone && <span>📱 {inf.phone}</span>}
                                        </div>
                                      )}
                                    </div>
                                    <div className="text-right">
                                      <p className="text-lg font-bold text-purple-400">{inf.commission_percent}%</p>
                                      <p className="text-[10px] text-[#6b7a99]">commission</p>
                                    </div>
                                  </div>
                                  <div className="grid grid-cols-4 gap-2 mt-2 text-center">
                                    <div className="bg-[#0a0e17] p-2 rounded">
                                      <p className="text-sm font-bold text-white">{inf.total_referrals}</p>
                                      <p className="text-[9px] text-[#6b7a99]">Referrals</p>
                                    </div>
                                    <div className="bg-[#0a0e17] p-2 rounded">
                                      <p className="text-sm font-bold text-green-400">₹{(inf.total_revenue_generated || 0).toLocaleString()}</p>
                                      <p className="text-[9px] text-[#6b7a99]">Revenue</p>
                                    </div>
                                    <div className="bg-[#0a0e17] p-2 rounded">
                                      <p className="text-sm font-bold text-amber-400">₹{(inf.total_commission_earned || 0).toLocaleString()}</p>
                                      <p className="text-[9px] text-[#6b7a99]">Earned</p>
                                    </div>
                                    <div className="bg-[#0a0e17] p-2 rounded">
                                      <p className="text-sm font-bold text-red-400">₹{(inf.pending_commission || 0).toLocaleString()}</p>
                                      <p className="text-[9px] text-[#6b7a99]">Pending</p>
                                    </div>
                                  </div>
                                  {/* Action Buttons Row 1: Edit, Mark Paid, Delete */}
                                  <div className="flex gap-2 mt-2">
                                    <Button 
                                      size="sm" 
                                      variant="outline" 
                                      onClick={() => setEditingInfluencer(inf.influencer_id)}
                                      className="text-xs"
                                    >
                                      Edit
                                    </Button>
                                    <div className="flex gap-1 flex-1">
                                      <Input
                                        type="number"
                                        placeholder="₹ Amount"
                                        value={payoutAmount}
                                        onChange={(e) => setPayoutAmount(e.target.value)}
                                        className="bg-[#0a0e17] border-[#1a2540] text-white rounded text-xs h-8 w-20"
                                      />
                                      <Button 
                                        size="sm" 
                                        onClick={() => markInfluencerPaid(inf.influencer_id)}
                                        className="bg-green-600 hover:bg-green-700 text-xs h-8"
                                        disabled={!payoutAmount || parseInt(payoutAmount) <= 0}
                                      >
                                        Pay
                                      </Button>
                                    </div>
                                    <Button 
                                      size="sm" 
                                      variant="destructive"
                                      onClick={() => deleteInfluencer(inf.influencer_id)}
                                      className="text-xs"
                                    >
                                      Del
                                    </Button>
                                  </div>
                                  {/* Action Buttons Row 2: Export, Gmail, WhatsApp */}
                                  <div className="flex gap-2 mt-2">
                                    <Button 
                                      size="sm" 
                                      variant="outline"
                                      onClick={() => exportInfluencerCSV(inf.influencer_id, inf.name)}
                                      className="text-xs flex-1"
                                    >
                                      📊 Export CSV
                                    </Button>
                                    <Button 
                                      size="sm" 
                                      onClick={() => sendInfluencerReport(inf.influencer_id)}
                                      className="bg-red-600 hover:bg-red-700 text-xs flex-1"
                                      disabled={!inf.email}
                                      title={inf.email ? 'Send via Gmail' : 'Add email first'}
                                    >
                                      ✉️ Gmail
                                    </Button>
                                    <Button 
                                      size="sm" 
                                      onClick={() => sendWhatsAppReport(inf.influencer_id)}
                                      className="bg-green-600 hover:bg-green-700 text-xs flex-1"
                                      disabled={!inf.phone}
                                      title={inf.phone ? 'Send via WhatsApp' : 'Add phone first'}
                                    >
                                      💬 WhatsApp
                                    </Button>
                                  </div>
                                </>
                              )}
                            </div>
                          ))
                        )}
                      </div>
                      
                      {/* Recent Referral Transactions */}
                      <div className="mt-4">
                        <div className="flex justify-between items-center mb-2">
                          <h3 className="text-sm font-semibold text-white">Recent Transactions</h3>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={exportReferralCSV}
                            className="text-xs h-7"
                          >
                            Export CSV
                          </Button>
                        </div>
                        {referralTransactions.length === 0 ? (
                          <p className="text-center text-[#6b7a99] text-xs py-4 bg-[#111827] rounded">No transactions yet. Transactions will appear here when users sign up with promo codes.</p>
                        ) : (
                          <div className="space-y-1 max-h-48 overflow-y-auto">
                            {referralTransactions.map((txn) => (
                              <div key={txn.transaction_id} className="flex justify-between items-center p-2 bg-[#111827] rounded text-xs">
                                <div>
                                  <span className={`px-1.5 py-0.5 rounded text-[9px] ${
                                    txn.type === 'user_referral' ? 'bg-blue-500/20 text-blue-400' : 'bg-purple-500/20 text-purple-400'
                                  }`}>
                                    {txn.type === 'user_referral' ? 'User Referral' : txn.promo_code || 'Influencer'}
                                  </span>
                                  <span className="ml-2 text-[#6b7a99]">{txn.referee_email}</span>
                                </div>
                                <div className="text-right">
                                  {txn.type === 'user_referral' ? (
                                    <span className="text-green-400">+7 days</span>
                                  ) : (
                                    <span className="text-amber-400">₹{txn.commission_amount} ({txn.commission_percent}%)</span>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      
                      {/* Admin Email Settings */}
                      <div className="mt-4 p-3 bg-[#111827] border border-[#1a2540] rounded-lg">
                        <h3 className="text-sm font-semibold text-white mb-2">Admin Email Settings</h3>
                        <p className="text-[10px] text-[#6b7a99] mb-2">This email will be used as the sender for influencer reports.</p>
                        <div className="flex gap-2">
                          <Input
                            type="email"
                            value={adminEmail}
                            onChange={(e) => setAdminEmail(e.target.value)}
                            className="flex-1 bg-[#0a0e17] border-[#1a2540] text-white rounded-lg text-sm"
                            placeholder="alphatradevission@gmail.com"
                          />
                          <Button
                            size="sm"
                            onClick={async () => {
                              try {
                                await apiClient.post('/admin/email-settings', {
                                  admin_password: password,
                                  admin_email: adminEmail,
                                  smtp_enabled: false
                                });
                                toast.success('Admin email saved!');
                              } catch (err) {
                                toast.error('Failed to save email');
                              }
                            }}
                            className="bg-blue-600 hover:bg-blue-700 text-xs"
                          >
                            Save
                          </Button>
                        </div>
                        <p className="text-[9px] text-amber-400 mt-2">
                          Note: Gmail/WhatsApp buttons will open compose windows. For automated emails, SMTP setup is required.
                        </p>
                      </div>
                      
                      {/* Consolidated User Referrals View */}
                      <div className="mt-4 p-3 bg-cyan-500/10 border border-cyan-500/30 rounded-lg">
                        <div className="flex justify-between items-center mb-3">
                          <div>
                            <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                              <Users className="w-4 h-4 text-cyan-400" />
                              All User Referrals
                            </h3>
                            <p className="text-[10px] text-[#6b7a99]">See all user-to-user referrals (not just influencers)</p>
                          </div>
                          <Button
                            size="sm"
                            onClick={() => {
                              setShowAllReferrals(!showAllReferrals);
                              if (!showAllReferrals && allReferrals.referrers.length === 0) {
                                fetchAllReferrals();
                              }
                            }}
                            className="bg-cyan-600 hover:bg-cyan-700 text-xs"
                            data-testid="view-all-referrals-btn"
                          >
                            {showAllReferrals ? 'Hide' : 'View All'}
                          </Button>
                        </div>
                        
                        {showAllReferrals && (
                          <>
                            {allReferralsLoading ? (
                              <div className="flex justify-center py-4">
                                <Loader2 className="w-5 h-5 animate-spin text-cyan-400" />
                              </div>
                            ) : (
                              <>
                                {/* Summary Stats */}
                                {allReferrals.summary && (
                                  <div className="grid grid-cols-4 gap-2 mb-3">
                                    <div className="bg-[#0a0e17] rounded-lg p-2 text-center">
                                      <p className="text-lg font-bold text-cyan-400">{allReferrals.summary.total_referrers || 0}</p>
                                      <p className="text-[9px] text-[#6b7a99]">Referrers</p>
                                    </div>
                                    <div className="bg-[#0a0e17] rounded-lg p-2 text-center">
                                      <p className="text-lg font-bold text-blue-400">{allReferrals.summary.total_referrals || 0}</p>
                                      <p className="text-[9px] text-[#6b7a99]">Total Referrals</p>
                                    </div>
                                    <div className="bg-[#0a0e17] rounded-lg p-2 text-center">
                                      <p className="text-lg font-bold text-green-400">{allReferrals.summary.total_successful || 0}</p>
                                      <p className="text-[9px] text-[#6b7a99]">Successful</p>
                                    </div>
                                    <div className="bg-[#0a0e17] rounded-lg p-2 text-center">
                                      <p className="text-lg font-bold text-amber-400">{allReferrals.summary.total_bonus_days_awarded || 0}</p>
                                      <p className="text-[9px] text-[#6b7a99]">Bonus Days</p>
                                    </div>
                                  </div>
                                )}
                                
                                {/* Referrers List */}
                                <div className="space-y-2 max-h-64 overflow-y-auto">
                                  {allReferrals.referrers.length === 0 ? (
                                    <p className="text-center text-[#6b7a99] text-xs py-4">No user referrals yet</p>
                                  ) : (
                                    allReferrals.referrers.map((ref, idx) => (
                                      <div key={idx} className="p-2 bg-[#0a0e17] rounded-lg">
                                        <div className="flex justify-between items-center">
                                          <div>
                                            <p className="text-xs font-medium text-white">{ref.referrer_name || ref.referrer_email}</p>
                                            <p className="text-[10px] text-[#6b7a99]">{ref.referrer_email}</p>
                                          </div>
                                          <div className="text-right">
                                            <code className="text-xs font-mono text-cyan-400 bg-cyan-500/10 px-2 py-0.5 rounded">{ref.referral_code}</code>
                                          </div>
                                        </div>
                                        <div className="flex gap-4 mt-1 text-[10px]">
                                          <span className="text-blue-400">{ref.total_referrals} referred</span>
                                          <span className="text-green-400">{ref.successful_referrals} paid</span>
                                          <span className="text-yellow-400">{ref.pending_referrals} pending</span>
                                          <span className="text-amber-400">+{ref.bonus_days_earned} days earned</span>
                                        </div>
                                        {ref.referred_users && ref.referred_users.length > 0 && (
                                          <div className="mt-1 text-[9px] text-[#6b7a99]">
                                            Recent: {ref.referred_users.slice(0, 3).map(u => u.email).join(', ')}
                                          </div>
                                        )}
                                      </div>
                                    ))
                                  )}
                                </div>
                                
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={fetchAllReferrals}
                                  className="w-full mt-2 text-xs"
                                >
                                  <RefreshCw className="w-3 h-3 mr-1" />
                                  Refresh
                                </Button>
                              </>
                            )}
                          </>
                        )}
                      </div>
                    </>
                  )}
                </div>
              )}
              
              {/* Messages/Broadcast Tab - Now uses refactored component */}
              {activeTab === 'messages' && (
                <MessagesTab password={password} />
              )}
              
              {/* Permissions Tab - Telegram/Zerodha permissions & Tick Data Management */}
              {activeTab === 'permissions' && (
                <PermissionsTab password={password} />
              )}
              
              {/* Algo Setup Tab - Google Sheets Integration */}
              {activeTab === 'algo-setup' && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                      <Table2 className="w-4 h-4 text-cyan-400" />
                      Google Sheets Configuration (Up to 5 Tabs)
                    </h3>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={fetchAlgoSetupConfigs}
                      disabled={algoSetupLoading}
                      className="text-xs"
                    >
                      <RefreshCw className={`w-3 h-3 mr-1 ${algoSetupLoading ? 'animate-spin' : ''}`} />
                      Refresh
                    </Button>
                  </div>
                  
                  <p className="text-[10px] text-[#6b7a99]">
                    Configure up to 5 Excel/Google Sheets tabs with custom alerts and column settings.
                  </p>
                  
                  {/* Instructions Box */}
                  <div className="p-3 bg-cyan-500/10 border border-cyan-500/30 rounded-lg mb-4">
                    <p className="text-[11px] text-cyan-400 font-semibold mb-2">How to set up:</p>
                    <ol className="text-[10px] text-[#9ca3af] space-y-1">
                      <li>1. Create a Google Sheet with your algo data</li>
                      <li>2. Click Share → Change "Restricted" to "Anyone with the link"</li>
                      <li>3. Copy the share link and paste below</li>
                      <li>4. Configure alerts and hidden columns as needed</li>
                      <li>5. Click Save - data will appear in user's Dashboard</li>
                    </ol>
                  </div>
                  
                  {/* Dynamic Sheet Configurations */}
                  {[0, 1, 2, 3, 4].map((idx) => {
                    const sheet = algoSheets[idx];
                    const sheetNum = idx + 1;
                    const colors = ['cyan', 'purple', 'emerald', 'amber', 'pink'];
                    const color = colors[idx];
                    const isConfigured = sheet.url && sheet.tab_name;
                    
                    return (
                      <div 
                        key={idx} 
                        className={`p-4 bg-[#111827] border border-${color}-500/30 rounded-xl ${!sheet.is_active && sheet.url ? 'opacity-60' : ''}`}
                      >
                        <div className="flex items-center justify-between mb-3">
                          <h4 className={`text-xs font-semibold text-${color}-400 flex items-center gap-2`}>
                            <span className={`w-5 h-5 bg-${color}-500/20 rounded flex items-center justify-center text-[10px]`}>{sheetNum}</span>
                            {sheetNum === 1 ? 'Primary Sheet' : sheetNum === 2 ? 'Secondary Sheet' : `Sheet ${sheetNum}`}
                            {isConfigured && sheet.is_active && <span className="ml-2 text-[9px] bg-green-500/20 text-green-400 px-1.5 py-0.5 rounded">Active</span>}
                          </h4>
                          <label className="flex items-center gap-2 text-[10px] text-[#6b7a99]">
                            <input
                              type="checkbox"
                              checked={sheet.is_active}
                              onChange={(e) => updateAlgoSheet(idx, { is_active: e.target.checked })}
                              className={`w-3 h-3 rounded accent-${color}-500`}
                            />
                            Active
                          </label>
                        </div>
                        
                        <div className="space-y-3">
                          {/* Basic Config */}
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className={`text-[10px] text-${color}-400 mb-1 block font-medium`}>Tab Name</label>
                              <Input
                                type="text"
                                value={sheet.tab_name}
                                onChange={(e) => updateAlgoSheet(idx, { tab_name: e.target.value })}
                                placeholder={`e.g., ${['ATV ALGO', 'BANKNIFTY', 'NIFTY', 'STOCKS', 'OPTIONS'][idx]}`}
                                className={`bg-[#0a0e17] border-${color}-500/30 text-white text-xs h-9`}
                              />
                            </div>
                            <div>
                              <label className={`text-[10px] text-${color}-400 mb-1 block font-medium`}>Sheet Tab (optional)</label>
                              <Input
                                type="text"
                                value={sheet.google_sheet_tab || ''}
                                onChange={(e) => updateAlgoSheet(idx, { google_sheet_tab: e.target.value })}
                                placeholder="e.g., Sheet1, Data"
                                className={`bg-[#0a0e17] border-${color}-500/30 text-white text-xs h-9`}
                              />
                            </div>
                          </div>
                          
                          <div>
                            <label className={`text-[10px] text-${color}-400 mb-1 block font-medium`}>Google Sheet Link</label>
                            <Input
                              type="url"
                              value={sheet.url}
                              onChange={(e) => updateAlgoSheet(idx, { url: e.target.value })}
                              placeholder="Paste your Google Sheet share link here..."
                              className={`bg-[#0a0e17] border-${color}-500/30 text-white text-xs h-9`}
                            />
                          </div>
                          
                          {/* Column Settings */}
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className={`text-[10px] text-${color}-400 mb-1 block font-medium`}>Hide Columns</label>
                              <Input
                                type="text"
                                value={sheet.hiddenColsInput || ''}
                                onChange={(e) => updateAlgoSheet(idx, { hiddenColsInput: e.target.value })}
                                placeholder="e.g., 1, 3, 5"
                                className={`bg-[#0a0e17] border-${color}-500/30 text-white text-xs h-9`}
                              />
                            </div>
                            <div>
                              <label className={`text-[10px] text-${color}-400 mb-1 block font-medium`}>Alert Columns</label>
                              <Input
                                type="text"
                                value={sheet.alertColsInput || ''}
                                onChange={(e) => updateAlgoSheet(idx, { alertColsInput: e.target.value })}
                                placeholder="e.g., 2, 4 (empty = all)"
                                className={`bg-[#0a0e17] border-${color}-500/30 text-white text-xs h-9`}
                              />
                            </div>
                          </div>
                          
                          {/* Alert Settings */}
                          <div className="flex flex-wrap items-center gap-4 pt-2 border-t border-[#1a2540]">
                            <label className="flex items-center gap-2 text-[10px] text-[#6b7a99]">
                              <input
                                type="checkbox"
                                checked={sheet.show_row_numbers ?? true}
                                onChange={(e) => updateAlgoSheet(idx, { show_row_numbers: e.target.checked })}
                                className={`w-3 h-3 rounded accent-${color}-500`}
                              />
                              Row Numbers
                            </label>
                            <label className="flex items-center gap-2 text-[10px] text-[#6b7a99]">
                              <input
                                type="checkbox"
                                checked={sheet.alerts_enabled ?? true}
                                onChange={(e) => updateAlgoSheet(idx, { alerts_enabled: e.target.checked })}
                                className={`w-3 h-3 rounded accent-${color}-500`}
                              />
                              Alerts
                            </label>
                            <label className="flex items-center gap-2 text-[10px] text-[#6b7a99]">
                              <input
                                type="checkbox"
                                checked={sheet.alert_sound ?? true}
                                onChange={(e) => updateAlgoSheet(idx, { alert_sound: e.target.checked })}
                                className={`w-3 h-3 rounded accent-${color}-500`}
                              />
                              Sound
                            </label>
                            <label className="flex items-center gap-2 text-[10px] text-[#6b7a99]">
                              <input
                                type="checkbox"
                                checked={sheet.highlight_changes ?? true}
                                onChange={(e) => updateAlgoSheet(idx, { highlight_changes: e.target.checked })}
                                className={`w-3 h-3 rounded accent-${color}-500`}
                              />
                              Highlight
                            </label>
                          </div>
                          
                          {/* Test and Save buttons */}
                          <div className="flex gap-2">
                            <Button
                              onClick={() => testGoogleSheetConnection(sheetNum, sheet)}
                              disabled={testingSheet === sheetNum || !sheet.url}
                              variant="outline"
                              size="sm"
                              className="flex-1 border-[#1a2540] text-[#6b7a99] hover:bg-[#1a2540] text-xs h-9"
                            >
                              {testingSheet === sheetNum ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />}
                              Test Connection
                            </Button>
                            <Button
                              onClick={() => saveAlgoSheetConfig(sheetNum, sheet)}
                              disabled={algoSetupLoading || !sheet.url || !sheet.tab_name}
                              size="sm"
                              className="flex-1 text-xs h-9"
                              style={{ backgroundColor: idx === 0 ? '#0891b2' : idx === 1 ? '#9333ea' : idx === 2 ? '#059669' : idx === 3 ? '#d97706' : '#db2777' }}
                            >
                              {algoSetupLoading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}
                              Save Sheet {sheetNum}
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              
              {/* Telegram Configuration Tab */}
              {activeTab === 'telegram' && (
                <TelegramConfigTab />
              )}
              
              {/* Reports Tab */}
              {activeTab === 'reports' && (
                <div className="space-y-4">
                  <div className="p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-xl">
                    <div className="flex items-center gap-2 mb-2">
                      <FileSpreadsheet className="w-5 h-5 text-emerald-400" />
                      <h3 className="text-lg font-bold text-white">Activity Reports</h3>
                    </div>
                    <p className="text-xs text-[#6b7a99]">
                      Download daily user activity reports in Excel format. Reports include logins, logouts, signups, security events, and more.
                    </p>
                  </div>
                  
                  {/* Daily Activity Export */}
                  <div className="p-4 bg-[#111827] border border-[#1a2540] rounded-xl">
                    <h4 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-cyan-400" />
                      Daily Activity Report
                    </h4>
                    
                    <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-end">
                      <div className="flex-1 w-full">
                        <Label className="text-[#6b7a99] text-xs mb-1 block">Select Date</Label>
                        <div className="relative">
                          <Input
                            type="date"
                            value={exportDate}
                            onChange={(e) => setExportDate(e.target.value)}
                            className="bg-[#0a0e17] border-[#1a2540] text-white text-sm w-full cursor-pointer [&::-webkit-calendar-picker-indicator]:filter [&::-webkit-calendar-picker-indicator]:invert [&::-webkit-calendar-picker-indicator]:opacity-70 [&::-webkit-calendar-picker-indicator]:cursor-pointer"
                            max={new Date().toISOString().split('T')[0]}
                            data-testid="export-date-input"
                          />
                        </div>
                        <p className="text-[9px] text-[#4a5a7a] mt-1">Click to open calendar picker</p>
                      </div>
                      
                      <Button
                        onClick={downloadDailyActivityReport}
                        disabled={exportLoading}
                        className="bg-emerald-500 hover:bg-emerald-600 text-white px-6"
                        data-testid="download-daily-report-btn"
                      >
                        {exportLoading ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Generating...
                          </>
                        ) : (
                          <>
                            <FileDown className="w-4 h-4 mr-2" />
                            Download Report
                          </>
                        )}
                      </Button>
                    </div>
                    
                    <div className="mt-4 p-3 bg-[#0a0e17] rounded-lg">
                      <p className="text-xs text-[#6b7a99] mb-2">Report includes:</p>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
                        <div className="flex items-center gap-1 text-cyan-400">
                          <Check className="w-3 h-3" /> User Logins
                        </div>
                        <div className="flex items-center gap-1 text-cyan-400">
                          <Check className="w-3 h-3" /> User Logouts
                        </div>
                        <div className="flex items-center gap-1 text-cyan-400">
                          <Check className="w-3 h-3" /> New Signups
                        </div>
                        <div className="flex items-center gap-1 text-cyan-400">
                          <Check className="w-3 h-3" /> Security Events
                        </div>
                        <div className="flex items-center gap-1 text-cyan-400">
                          <Check className="w-3 h-3" /> Payment Events
                        </div>
                        <div className="flex items-center gap-1 text-cyan-400">
                          <Check className="w-3 h-3" /> Daily Summary
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// FLOATING CONTACT ICONS COMPONENT
// =============================================================================
const FloatingContactIcons = () => {
  const [contacts, setContacts] = useState(null);
  const [showTooltip, setShowTooltip] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  
  useEffect(() => {
    let isMounted = true;
    
    const loadContacts = async () => {
      try {
        const response = await apiClient.get('/contact/settings');
        if (isMounted && response.data && response.data.show_contacts) {
          setContacts(response.data);
        }
      } catch (err) {
        console.error('Failed to load contacts:', err);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };
    
    // Small delay to ensure component is properly mounted
    const timer = setTimeout(loadContacts, 100);
    
    return () => {
      isMounted = false;
      clearTimeout(timer);
    };
  }, []);
  
  // Show nothing while loading or if no contacts
  if (isLoading || !contacts || !contacts.show_contacts) return null;
  
  const handleWhatsApp = (e) => {
    e.preventDefault();
    if (contacts.whatsapp_number) {
      const message = encodeURIComponent(contacts.whatsapp_message || 'Hello!');
      // Ensure number has only digits
      let number = contacts.whatsapp_number.replace(/[^0-9]/g, '');
      // If number is 10 digits (no country code), assume India +91
      if (number.length === 10) {
        number = '91' + number;
      }
      // For wa.me, the number should be full international format without + 
      const url = `https://wa.me/${number}?text=${message}`;
      // Use location.href to avoid popup blocker
      window.location.href = url;
    }
  };
  
  const handlePhone = () => {
    if (contacts.phone_number) {
      // Format phone number with +91 if not present
      let phone = contacts.phone_number;
      if (!phone.startsWith('+')) {
        phone = '+91' + phone.replace(/[^0-9]/g, '');
      }
      window.location.href = `tel:${phone}`;
      toast.info(contacts.phone_busy_message || 'Admin will contact you shortly.', { duration: 5000 });
    }
  };
  
  const handleTelegram = (e) => {
    e.preventDefault();
    if (contacts.telegram_group) {
      window.location.href = contacts.telegram_group;
    }
  };
  
  const handleYouTube = (e) => {
    e.preventDefault();
    if (contacts.youtube_channel) {
      // Use location.href to avoid popup blocker
      window.location.href = contacts.youtube_channel;
    }
  };
  
  const handleLinkedIn = (e) => {
    e.preventDefault();
    if (contacts.linkedin_profile) {
      window.location.href = contacts.linkedin_profile;
    }
  };
  
  const handleInstagram = () => {
    if (contacts.instagram_profile) {
      window.open(contacts.instagram_profile, '_blank');
    }
  };
  
  return (
    <div className="fixed right-4 top-1/2 -translate-y-1/2 flex flex-col gap-2 z-40">
      {/* WhatsApp */}
      {contacts.whatsapp_number && (
        <div className="relative">
          <button
            onClick={handleWhatsApp}
            onMouseEnter={() => setShowTooltip('whatsapp')}
            onMouseLeave={() => setShowTooltip(null)}
            className="w-10 h-10 rounded-full bg-green-500 hover:bg-green-600 flex items-center justify-center shadow-lg transition-transform hover:scale-110"
            data-testid="contact-whatsapp"
          >
            <MessageCircle className="w-5 h-5 text-white" />
          </button>
          {showTooltip === 'whatsapp' && (
            <div className="absolute right-12 top-1/2 -translate-y-1/2 bg-[#111827] border border-[#1a2540] rounded-lg px-3 py-2 whitespace-nowrap">
              <p className="text-xs text-white">WhatsApp</p>
            </div>
          )}
        </div>
      )}
      
      {/* Phone */}
      {contacts.phone_number && (
        <div className="relative">
          <button
            onClick={handlePhone}
            onMouseEnter={() => setShowTooltip('phone')}
            onMouseLeave={() => setShowTooltip(null)}
            className="w-10 h-10 rounded-full bg-blue-500 hover:bg-blue-600 flex items-center justify-center shadow-lg transition-transform hover:scale-110"
            data-testid="contact-phone"
          >
            <Phone className="w-5 h-5 text-white" />
          </button>
          {showTooltip === 'phone' && (
            <div className="absolute right-12 top-1/2 -translate-y-1/2 bg-[#111827] border border-[#1a2540] rounded-lg px-3 py-2 whitespace-nowrap">
              <p className="text-xs text-white">Call Us</p>
            </div>
          )}
        </div>
      )}
      
      {/* Telegram */}
      {contacts.telegram_group && (
        <div className="relative">
          <button
            onClick={handleTelegram}
            onMouseEnter={() => setShowTooltip('telegram')}
            onMouseLeave={() => setShowTooltip(null)}
            className="w-10 h-10 rounded-full bg-sky-500 hover:bg-sky-600 flex items-center justify-center shadow-lg transition-transform hover:scale-110"
            data-testid="contact-telegram"
          >
            <Send className="w-5 h-5 text-white" />
          </button>
          {showTooltip === 'telegram' && (
            <div className="absolute right-12 top-1/2 -translate-y-1/2 bg-[#111827] border border-[#1a2540] rounded-lg px-3 py-2 whitespace-nowrap">
              <p className="text-xs text-white">Telegram</p>
            </div>
          )}
        </div>
      )}
      
      {/* YouTube */}
      {contacts.youtube_channel && (
        <div className="relative">
          <button
            onClick={handleYouTube}
            onMouseEnter={() => setShowTooltip('youtube')}
            onMouseLeave={() => setShowTooltip(null)}
            className="w-10 h-10 rounded-full bg-red-500 hover:bg-red-600 flex items-center justify-center shadow-lg transition-transform hover:scale-110"
            data-testid="contact-youtube"
          >
            <Youtube className="w-5 h-5 text-white" />
          </button>
          {showTooltip === 'youtube' && (
            <div className="absolute right-12 top-1/2 -translate-y-1/2 bg-[#111827] border border-[#1a2540] rounded-lg px-3 py-2 whitespace-nowrap">
              <p className="text-xs text-white">YouTube</p>
            </div>
          )}
        </div>
      )}
      
      {/* LinkedIn */}
      {contacts.linkedin_profile && (
        <div className="relative">
          <button
            onClick={handleLinkedIn}
            onMouseEnter={() => setShowTooltip('linkedin')}
            onMouseLeave={() => setShowTooltip(null)}
            className="w-9 h-9 rounded-full bg-[#0077B5] hover:bg-[#006399] flex items-center justify-center shadow-lg transition-transform hover:scale-110"
            data-testid="contact-linkedin"
          >
            <Linkedin className="w-4 h-4 text-white" />
          </button>
          {showTooltip === 'linkedin' && (
            <div className="absolute right-12 top-1/2 -translate-y-1/2 bg-[#111827] border border-[#1a2540] rounded-lg px-3 py-2 whitespace-nowrap">
              <p className="text-xs text-white">LinkedIn</p>
            </div>
          )}
        </div>
      )}
      
      {/* Instagram */}
      {contacts.instagram_profile && (
        <div className="relative">
          <button
            onClick={handleInstagram}
            onMouseEnter={() => setShowTooltip('instagram')}
            onMouseLeave={() => setShowTooltip(null)}
            className="w-9 h-9 rounded-full bg-gradient-to-br from-[#f09433] via-[#e6683c] to-[#bc1888] hover:opacity-90 flex items-center justify-center shadow-lg transition-transform hover:scale-110"
            data-testid="contact-instagram"
          >
            <Instagram className="w-4 h-4 text-white" />
          </button>
          {showTooltip === 'instagram' && (
            <div className="absolute right-12 top-1/2 -translate-y-1/2 bg-[#111827] border border-[#1a2540] rounded-lg px-3 py-2 whitespace-nowrap">
              <p className="text-xs text-white">Instagram</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// =============================================================================
// GO PREMIUM TAB COMPONENT
// =============================================================================
const GoPremiumTab = () => {
  const [plans, setPlans] = useState({});
  const [upiSettings, setUpiSettings] = useState({ upi_id: '', qr_code_url: '', payment_instructions: '', bank_details: null });
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  // Payment form
  const [email, setEmail] = useState('');
  const [utrNumber, setUtrNumber] = useState('');
  const [screenshotUrl, setScreenshotUrl] = useState('');
  const [screenshotFile, setScreenshotFile] = useState(null);
  const [screenshotPreview, setScreenshotPreview] = useState('');
  const [uploadingScreenshot, setUploadingScreenshot] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [promoCodeValid, setPromoCodeValid] = useState('');
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [showBankDetails, setShowBankDetails] = useState(false);
  
  // Handle screenshot file selection
  const handleScreenshotSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    if (!['image/jpeg', 'image/png', 'image/jpg', 'image/webp'].includes(file.type)) {
      toast.error('Please upload a valid image (JPEG, PNG, or WebP)');
      return;
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image too large. Maximum 5MB allowed.');
      return;
    }
    
    // Show preview
    const reader = new FileReader();
    reader.onload = (e) => setScreenshotPreview(e.target.result);
    reader.readAsDataURL(file);
    setScreenshotFile(file);
    
    // Upload file
    setUploadingScreenshot(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await apiClient.post('/payment/upload-screenshot', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (response.data.success) {
        setScreenshotUrl(response.data.url);
        toast.success('Screenshot uploaded successfully!');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload screenshot');
      setScreenshotFile(null);
      setScreenshotPreview('');
    }
    setUploadingScreenshot(false);
  };
  
  // Clear screenshot
  const clearScreenshot = () => {
    setScreenshotFile(null);
    setScreenshotPreview('');
    setScreenshotUrl('');
  };
  
  // Load payment settings
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const response = await apiClient.get('/payment/settings');
        setPlans(response.data.plans || {});
        setUpiSettings({
          upi_id: response.data.upi_id || '',
          qr_code_url: response.data.qr_code_url || '',
          payment_instructions: response.data.payment_instructions || '',
          bank_details: response.data.bank_details || null
        });
      } catch (err) {
        console.error('Failed to load payment settings:', err);
      }
      setLoading(false);
    };
    loadSettings();
  }, []);
  
  // Validate promo code when it changes
  useEffect(() => {
    const validateCode = async () => {
      if (promoCode.length >= 3) {
        try {
          const response = await apiClient.get(`/referral/validate/${promoCode}`);
          if (response.data.valid && response.data.type === 'influencer') {
            setPromoCodeValid(response.data.benefit);
          } else {
            setPromoCodeValid('');
          }
        } catch {
          setPromoCodeValid('');
        }
      } else {
        setPromoCodeValid('');
      }
    };
    
    const debounce = setTimeout(validateCode, 500);
    return () => clearTimeout(debounce);
  }, [promoCode]);
  
  // Check payment status for email
  const checkPaymentStatus = async () => {
    if (!email) return;
    try {
      const response = await apiClient.get(`/payment/user-status/${email}`);
      if (response.data.has_payment) {
        setPaymentStatus(response.data.payment);
      }
    } catch (err) {
      console.error('Failed to check payment:', err);
    }
  };
  
  // Submit payment
  const handleSubmitPayment = async () => {
    if (!email || !selectedPlan) {
      toast.error('Please enter email and select a plan');
      return;
    }
    if (!utrNumber && !screenshotUrl) {
      toast.error('Please enter UTR number or upload screenshot');
      return;
    }
    
    setSubmitting(true);
    try {
      const plan = plans[selectedPlan];
      const response = await apiClient.post('/payment/submit', {
        user_email: email,
        plan_id: selectedPlan,
        utr_number: utrNumber || null,
        screenshot_url: screenshotUrl || null,
        amount: plan.price,
        promo_code: promoCode || null
      });
      
      if (response.data.success) {
        let description = 'You will get subscription approval within 24 hours after admin confirmation.';
        if (response.data.promo_applied) {
          description = `${response.data.promo_applied}. ${description}`;
        }
        toast.success('Payment submitted successfully!', {
          duration: 8000,
          description
        });
        setPaymentStatus({
          status: 'pending',
          payment_id: response.data.payment_id,
          plan_name: plan.name
        });
        setShowPaymentForm(false);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit payment');
    }
    setSubmitting(false);
  };
  
  // Sort plans by duration
  const sortedPlans = Object.entries(plans).sort((a, b) => a[1].duration_days - b[1].duration_days);
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-10">
        <Loader2 className="w-6 h-6 animate-spin text-cyan-400" />
      </div>
    );
  }
  
  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="text-center">
        <div className="inline-flex items-center gap-2 px-3 py-1 bg-amber-500/10 border border-amber-500/30 rounded-full mb-1">
          <Crown className="w-3 h-3 text-amber-400" />
          <span className="text-[10px] font-medium text-amber-400">PREMIUM ACCESS</span>
        </div>
      </div>
      
      {/* Payment Status */}
      {paymentStatus && (
        <div className={`p-2 rounded-lg border text-xs ${
          paymentStatus.status === 'pending' ? 'bg-yellow-500/10 border-yellow-500/30' :
          paymentStatus.status === 'approved' ? 'bg-green-500/10 border-green-500/30' :
          'bg-red-500/10 border-red-500/30'
        }`}>
          <div className="flex items-center gap-2">
            {paymentStatus.status === 'pending' && <Clock className="w-3 h-3 text-yellow-400" />}
            {paymentStatus.status === 'approved' && <CheckCircle className="w-3 h-3 text-green-400" />}
            {paymentStatus.status === 'rejected' && <XCircle className="w-3 h-3 text-red-400" />}
            <span className={paymentStatus.status === 'pending' ? 'text-yellow-400' : paymentStatus.status === 'approved' ? 'text-green-400' : 'text-red-400'}>
              {paymentStatus.status === 'pending' ? 'Under Verification' : paymentStatus.status === 'approved' ? 'Approved!' : 'Rejected'}
            </span>
          </div>
        </div>
      )}
      
      {/* Two Column Layout */}
      <div className="grid grid-cols-2 gap-3">
        {/* Left: Plans */}
        <div className="space-y-2">
          <p className="text-[10px] text-[#6b7a99] font-medium">SELECT PLAN</p>
          {sortedPlans.map(([planId, plan]) => (
            <button
              key={planId}
              onClick={() => setSelectedPlan(planId)}
              className={`w-full p-2 rounded-lg border transition-all text-left ${
                selectedPlan === planId
                  ? 'bg-cyan-500/10 border-cyan-500/50 ring-1 ring-cyan-500/30'
                  : 'bg-[#111827] border-[#1a2540] hover:border-[#2a3550]'
              }`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-xs font-semibold text-white">{plan.name}</span>
                  <span className="ml-1 text-[9px] text-[#6b7a99]">({plan.duration_days}d)</span>
                </div>
                <span className="text-sm font-bold text-green-400">₹{plan.price?.toLocaleString()}</span>
              </div>
            </button>
          ))}
          
          {sortedPlans.length === 0 && (
            <div className="text-center py-4 text-[#6b7a99]">
              <CreditCard className="w-6 h-6 mx-auto mb-1 opacity-50" />
              <p className="text-xs">No plans</p>
            </div>
          )}
        </div>
        
        {/* Right: Payment Details */}
        <div className="space-y-2">
          {selectedPlan && upiSettings.upi_id ? (
            <>
              <p className="text-[10px] text-[#6b7a99] font-medium">PAY VIA UPI</p>
              
              {/* QR Code - Smaller */}
              {upiSettings.qr_code_url && (
                <div className="flex justify-center">
                  <div className="p-1 bg-white rounded-lg">
                    <img src={upiSettings.qr_code_url} alt="QR" className="w-28 h-28 object-contain" />
                  </div>
                </div>
              )}
              
              {/* UPI ID */}
              <div className="flex items-center gap-1 p-1.5 bg-[#0a0e17] rounded text-xs">
                <span className="text-[#6b7a99]">UPI:</span>
                <span className="font-mono text-cyan-400 flex-1 truncate">{upiSettings.upi_id}</span>
                <button
                  onClick={() => copyToClipboard(upiSettings.upi_id, 'UPI ID copied!')}
                  className="p-0.5 hover:bg-[#1a2540] rounded"
                >
                  <Copy className="w-3 h-3 text-[#6b7a99]" />
                </button>
              </div>
              
              {/* Amount */}
              <div className="flex items-center justify-between p-1.5 bg-green-500/10 border border-green-500/30 rounded text-xs">
                <span className="text-green-400">Amount:</span>
                <span className="font-bold text-green-400">₹{plans[selectedPlan]?.price?.toLocaleString()}</span>
              </div>
              
              {/* Bank Details Toggle */}
              {upiSettings.bank_details && (
                <button
                  onClick={() => setShowBankDetails(!showBankDetails)}
                  className="w-full text-[10px] text-blue-400 hover:text-blue-300 py-1"
                >
                  {showBankDetails ? '▼ Hide Bank Details' : '▶ Show Bank Details'}
                </button>
              )}
              
              {/* Bank Details */}
              {showBankDetails && upiSettings.bank_details && (
                <div className="p-2 bg-[#0a0e17] rounded border border-[#1a2540] text-[10px] space-y-1">
                  <p className="text-[#6b7a99]">Bank: <span className="text-white">{upiSettings.bank_details.bank_name}</span></p>
                  <p className="text-[#6b7a99]">Name: <span className="text-white">{upiSettings.bank_details.account_holder}</span></p>
                  <p className="text-[#6b7a99]">A/C: <span className="text-white font-mono">{upiSettings.bank_details.account_number}</span></p>
                  <p className="text-[#6b7a99]">IFSC: <span className="text-white font-mono">{upiSettings.bank_details.ifsc_code}</span></p>
                </div>
              )}
              
              {/* Submit Button */}
              <Button
                onClick={() => setShowPaymentForm(true)}
                className="w-full bg-gradient-to-r from-green-500 to-emerald-600 text-white text-xs font-semibold h-8"
              >
                <CheckCircle className="w-3 h-3 mr-1" />
                I've Paid
              </Button>
            </>
          ) : selectedPlan ? (
            <div className="flex items-center justify-center h-full text-[#6b7a99] text-xs">
              <p>Payment not configured</p>
            </div>
          ) : (
            <div className="flex items-center justify-center h-full text-[#6b7a99] text-xs">
              <p>← Select a plan</p>
            </div>
          )}
        </div>
      </div>
      
      {/* Payment Submission Form Modal */}
      {showPaymentForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
          <div className="w-full max-w-md bg-[#0a0e17] border border-[#1a2540] rounded-2xl p-5">
            <div className="flex items-center justify-between mb-4 pb-3 border-b border-[#1a2540]">
              <h3 className="text-xl font-bold text-white flex items-center gap-2">
                <CheckCircle className="w-6 h-6 text-green-400" />
                Submit Payment Proof
              </h3>
              <button onClick={() => { setShowPaymentForm(false); clearScreenshot(); }} className="text-[#6b7a99] hover:text-white p-1 hover:bg-white/10 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-3">
              {/* Email */}
              <div>
                <Label className="text-[#6b7a99] text-xs">Your Email (Registered) *</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4a5a7a]" />
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onBlur={checkPaymentStatus}
                    className="pl-10 bg-[#111827] border-[#1a2540] text-white rounded-lg"
                    placeholder="your@email.com"
                    data-testid="payment-email-input"
                  />
                </div>
              </div>
              
              {/* Plan Info */}
              <div className="p-2 bg-cyan-500/10 border border-cyan-500/30 rounded-lg">
                <div className="flex justify-between items-center">
                  <span className="text-xs text-cyan-400">Selected Plan:</span>
                  <span className="text-sm font-semibold text-white">{plans[selectedPlan]?.name}</span>
                </div>
                <div className="flex justify-between items-center mt-1">
                  <span className="text-xs text-cyan-400">Amount:</span>
                  <span className="text-sm font-bold text-green-400">₹{plans[selectedPlan]?.price?.toLocaleString()}</span>
                </div>
              </div>
              
              {/* UTR Number */}
              <div>
                <Label className="text-[#6b7a99] text-xs">UTR / Transaction Number</Label>
                <Input
                  type="text"
                  value={utrNumber}
                  onChange={(e) => setUtrNumber(e.target.value)}
                  className="mt-1 bg-[#111827] border-[#1a2540] text-white rounded-lg font-mono"
                  placeholder="Enter 12-digit UTR number"
                  data-testid="payment-utr-input"
                />
              </div>
              
              {/* OR Divider */}
              <div className="flex items-center gap-3">
                <div className="flex-1 h-px bg-[#1a2540]"></div>
                <span className="text-xs text-[#4a5a7a]">OR</span>
                <div className="flex-1 h-px bg-[#1a2540]"></div>
              </div>
              
              {/* Screenshot Upload */}
              <div>
                <Label className="text-[#6b7a99] text-xs">Upload Payment Screenshot</Label>
                
                {/* Preview */}
                {screenshotPreview ? (
                  <div className="mt-2 relative">
                    <img 
                      src={screenshotPreview} 
                      alt="Payment Screenshot" 
                      className="w-full max-h-40 object-contain rounded-lg border border-[#1a2540]"
                    />
                    <button
                      onClick={clearScreenshot}
                      className="absolute top-2 right-2 p-1 bg-red-500/80 hover:bg-red-500 rounded-full"
                    >
                      <X className="w-4 h-4 text-white" />
                    </button>
                    {uploadingScreenshot && (
                      <div className="absolute inset-0 bg-black/50 flex items-center justify-center rounded-lg">
                        <Loader2 className="w-6 h-6 animate-spin text-white" />
                      </div>
                    )}
                    {screenshotUrl && !uploadingScreenshot && (
                      <div className="absolute bottom-2 left-2 px-2 py-1 bg-green-500/80 rounded text-[10px] text-white flex items-center gap-1">
                        <CheckCircle className="w-3 h-3" />
                        Uploaded
                      </div>
                    )}
                  </div>
                ) : (
                  <label className="mt-2 flex flex-col items-center justify-center w-full h-24 border-2 border-dashed border-[#1a2540] rounded-lg cursor-pointer hover:border-cyan-500/50 hover:bg-cyan-500/5 transition-colors">
                    <div className="flex flex-col items-center justify-center pt-2 pb-3">
                      <Upload className="w-6 h-6 text-[#4a5a7a] mb-1" />
                      <p className="text-xs text-[#6b7a99]">Click to upload screenshot</p>
                      <p className="text-[10px] text-[#4a5a7a]">PNG, JPG, WebP (max 5MB)</p>
                    </div>
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/jpg,image/webp"
                      onChange={handleScreenshotSelect}
                      className="hidden"
                      data-testid="payment-screenshot-input"
                    />
                  </label>
                )}
              </div>
              
              {/* Promo Code */}
              <div>
                <Label className="text-[#6b7a99] text-xs">Promo/Influencer Code (Optional)</Label>
                <div className="relative mt-1">
                  <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4a5a7a]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                  </svg>
                  <Input
                    type="text"
                    value={promoCode}
                    onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                    className="pl-10 bg-[#111827] border-[#1a2540] text-white rounded-lg"
                    placeholder="Enter promo code (if any)"
                    data-testid="payment-promo-input"
                  />
                </div>
                {promoCodeValid && (
                  <p className="mt-1 text-[10px] text-green-400">{promoCodeValid}</p>
                )}
              </div>
              
              {/* Submit Button */}
              <Button
                onClick={handleSubmitPayment}
                disabled={submitting || !email || (!utrNumber && !screenshotUrl)}
                className="w-full bg-gradient-to-r from-green-500 to-emerald-600 text-white font-semibold mt-2"
                data-testid="submit-payment-btn"
              >
                {submitting ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <CheckCircle className="w-4 h-4 mr-2" />
                )}
                Submit for Verification
              </Button>
              
              <p className="text-[9px] text-center text-[#4a5a7a]">
                Your subscription will be activated after payment verification (usually within 24 hours)
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// =============================================================================
// MAIN LOGIN PAGE
// =============================================================================
export default function Login({ onLoginSuccess }) {
  const [showSplash, setShowSplash] = useState(true);
  
  // Check URL query parameter for initial tab
  const getInitialTab = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const tabParam = urlParams.get('tab');
    if (tabParam === 'premium') {
      // Clean up URL without reloading
      window.history.replaceState({}, document.title, window.location.pathname);
      return 'premium';
    }
    return 'signin';
  };
  
  const [activeTab, setActiveTab] = useState(getInitialTab); // 'signin', 'signup', 'premium'
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  
  // Secret key tracking (type "7" seven times)
  const [secretKeyCount, setSecretKeyCount] = useState(0);
  
  // Form fields
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [referralCode, setReferralCode] = useState('');
  const [referralCodeValid, setReferralCodeValid] = useState('');
  const [error, setError] = useState('');
  const [errorDetail, setErrorDetail] = useState(null);  // Classified error with fix steps
  const [showErrorHelp, setShowErrorHelp] = useState(false);  // Controls the "Why did this fail?" modal
  
  // Terms and Conditions
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);

  // State for security code modal
  const [showSecurityModal, setShowSecurityModal] = useState(false);
  const [securityCodeInput, setSecurityCodeInput] = useState('');

  // Listen for secret key (pressing "7" seven times)
  useEffect(() => {
    const handleKeyPress = (e) => {
      // Don't trigger if typing in an input field
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      
      // Check if admin security is enabled
      const securityEnabled = localStorage.getItem('admin_security_enabled') === 'true';
      const securityCode = localStorage.getItem('admin_security_code') || '';
      
      if (e.key === '7') {
        setSecretKeyCount(prev => {
          const newCount = prev + 1;
          if (newCount >= 7) {
            // If security is enabled, show security modal
            if (securityEnabled && securityCode) {
              setShowSecurityModal(true);
              setSecurityCodeInput('');
            } else {
              setShowAdminPanel(true);
            }
            return 0;
          }
          return newCount;
        });
        
        // Reset count after 2 seconds of no input
        setTimeout(() => setSecretKeyCount(0), 2000);
      } else {
        setSecretKeyCount(0);
      }
    };

    window.addEventListener('keypress', handleKeyPress);
    return () => window.removeEventListener('keypress', handleKeyPress);
  }, []);

  // Handle security code verification
  const verifySecurityCode = () => {
    const savedCode = localStorage.getItem('admin_security_code') || '';
    if (securityCodeInput === savedCode) {
      setShowSecurityModal(false);
      setShowAdminPanel(true);
      toast.success('Access granted!');
    } else {
      toast.error('Invalid security code');
    }
  };

  // Classify a sign-in error into a human-friendly explanation + fix steps.
  // This replaces the generic "Something went wrong" with the actual reason.
  const classifyLoginError = (err) => {
    const status = err?.response?.status;
    const detail = err?.response?.data?.detail;
    const code = err?.code;
    const msg = err?.message || '';

    // 1. Network / CORS / offline — no response object at all
    if (!err?.response && (code === 'ERR_NETWORK' || code === 'ECONNABORTED' || /Network Error/i.test(msg))) {
      return {
        code: 'NETWORK_ERROR',
        title: 'Cannot reach server',
        message: 'Browser could not connect to the API. You may be offline, or the server domain is misconfigured.',
        fixes: [
          'Check your internet connection (Wi-Fi / mobile data)',
          'Reload the page (Ctrl/Cmd + R)',
          'If using a custom domain, the API URL may not match the page URL (CORS) — contact admin',
        ],
        canRetry: true,
      };
    }

    // 2. Wrong credentials
    if (status === 401) {
      return {
        code: 'INVALID_CREDENTIALS',
        title: 'Invalid email or password',
        message: detail || 'The email or password you entered is incorrect.',
        fixes: [
          'Check that Caps Lock is OFF',
          'Verify you are using the email you registered with',
          'Use the eye icon to reveal the password and double-check it',
          'Contact admin if you forgot your password',
        ],
        canRetry: true,
      };
    }

    // 3. Brute-force lockout
    if (status === 429) {
      return {
        code: 'ACCOUNT_LOCKED',
        title: 'Too many failed attempts',
        message: detail || 'Your account has been temporarily locked due to multiple wrong password attempts.',
        fixes: [
          'Wait for the lockout to expire (usually 15 minutes)',
          'Make sure you have the correct password before retrying',
          'Contact admin if you need immediate access',
        ],
        canRetry: false,
      };
    }

    // 4. Access denied — pending / deactivated / expired
    if (status === 403) {
      const dl = (detail || '').toLowerCase();
      if (dl.includes('pending')) {
        return {
          code: 'PENDING_APPROVAL',
          title: 'Account pending admin approval',
          message: detail,
          fixes: [
            'Your account was created but admin has not approved it yet',
            'Contact admin via WhatsApp / Telegram to expedite approval',
          ],
          canRetry: false,
        };
      }
      if (dl.includes('expired')) {
        return {
          code: 'SUBSCRIPTION_EXPIRED',
          title: 'Subscription / trial expired',
          message: detail,
          fixes: [
            'Click "Subscribe" in the sign-up tab to renew your plan',
            'Contact admin if you believe this is an error',
          ],
          canRetry: false,
        };
      }
      if (dl.includes('deactivat') || dl.includes('disabled')) {
        return {
          code: 'ACCOUNT_DEACTIVATED',
          title: 'Account deactivated',
          message: detail,
          fixes: [
            'Your account has been disabled by admin',
            'Contact admin to reactivate',
          ],
          canRetry: false,
        };
      }
      return {
        code: 'ACCESS_DENIED',
        title: 'Access denied',
        message: detail || 'You do not have permission to sign in right now.',
        fixes: ['Contact admin via WhatsApp/Telegram for assistance'],
        canRetry: false,
      };
    }

    // 5. Bad request (missing fields, invalid format)
    if (status === 400 || status === 422) {
      return {
        code: 'BAD_REQUEST',
        title: 'Invalid login request',
        message: detail || 'The server rejected the request.',
        fixes: [
          'Make sure both email and password are filled in',
          'Email should be in valid format (e.g., user@example.com)',
          'Try again after refreshing the page',
        ],
        canRetry: true,
      };
    }

    // 6. Server error
    if (status >= 500) {
      return {
        code: 'SERVER_ERROR',
        title: 'Server temporarily unavailable',
        message: detail || `Server returned ${status}. It may be restarting.`,
        fixes: [
          'Wait 30-60 seconds and try again',
          'If the issue persists beyond 5 minutes, contact admin',
        ],
        canRetry: true,
      };
    }

    // Fallback
    return {
      code: status ? `HTTP_${status}` : 'UNKNOWN',
      title: 'Sign-in failed',
      message: detail || msg || 'Unknown error',
      fixes: [
        'Try again in a few seconds',
        'Refresh the page',
        'If the problem persists, contact admin',
      ],
      canRetry: true,
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setErrorDetail(null);

    try {
      if (activeTab === 'signin') {
        const response = await apiClient.post('/user/login', { email, password });
        if (response.data.success) {
          toast.success(`Welcome back, ${response.data.user.name}!`);
          
          // Pass data to parent (App.js) to handle SEBI disclaimer first
          // Don't set auth tokens here - let App.js handle it after disclaimer
          if (onLoginSuccess) {
            onLoginSuccess(response.data);
            // Don't redirect here - App.js will handle it after SEBI disclaimer
          } else {
            // Fallback if no onLoginSuccess handler
            localStorage.setItem('auth_token', response.data.access_token);
            localStorage.setItem('session_id', response.data.session_id);
            localStorage.setItem('user', JSON.stringify(response.data.user));
            window.location.href = '/';
          }
        }
      } else if (activeTab === 'signup') {
        if (!name.trim()) {
          setError('Please enter your name');
          setIsLoading(false);
          return;
        }
        if (!phone.trim()) {
          setError('Please enter your phone number');
          setIsLoading(false);
          return;
        }
        if (!termsAccepted) {
          setError('Please accept the Terms & Conditions to continue');
          setIsLoading(false);
          return;
        }
        const response = await apiClient.post('/user/signup', { 
          email, 
          password, 
          name,
          phone,
          address,
          city,
          referral_code: referralCode || undefined
        });
        if (response.data.success) {
          // Check if account is pending approval
          if (response.data.pending_approval) {
            let message = 'Account created! Please wait for admin approval.';
            if (response.data.referral_applied) {
              message = `Account created! ${response.data.referral_applied}. Please wait for admin approval.`;
            }
            toast.success(message, {
              duration: 5000,
              description: 'You will be able to login once admin activates your account.'
            });
            // Don't redirect - show message and switch to login tab
            setActiveTab('signin');
            setError('');
          } else {
            // If not pending (shouldn't happen with new flow), redirect
            localStorage.setItem('auth_token', response.data.access_token);
            localStorage.setItem('user', JSON.stringify(response.data.user));
            toast.success('Account created successfully!');
            if (onLoginSuccess) onLoginSuccess(response.data);
            window.location.href = '/';
          }
        }
      }
    } catch (err) {
      const detail = classifyLoginError(err);
      setErrorDetail(detail);
      setError(detail.title);  // Show the SPECIFIC reason (not "Something went wrong")
      // Surface a toast for blocking/critical errors that prevent retry
      if (!detail.canRetry || ['SUBSCRIPTION_EXPIRED', 'PENDING_APPROVAL', 'ACCOUNT_DEACTIVATED', 'ACCOUNT_LOCKED'].includes(detail.code)) {
        toast.error(detail.message || detail.title, { duration: 6000 });
      }
    }
    setIsLoading(false);
  };
  
  // Validate referral code when it changes
  useEffect(() => {
    const validateCode = async () => {
      if (referralCode.length >= 4) {
        try {
          const response = await apiClient.get(`/referral/validate/${referralCode}`);
          if (response.data.valid) {
            setReferralCodeValid(response.data.benefit);
          } else {
            setReferralCodeValid('');
          }
        } catch {
          setReferralCodeValid('');
        }
      } else {
        setReferralCodeValid('');
      }
    };
    
    const debounce = setTimeout(validateCode, 500);
    return () => clearTimeout(debounce);
  }, [referralCode]);

  if (showSplash) {
    return <SplashScreen onComplete={() => setShowSplash(false)} />;
  }

  return (
    <div className="min-h-screen w-full bg-[#030508] flex items-center justify-center p-6 overflow-y-auto">
      {/* Floating Contact Icons - Using new expandable dropdown */}
      <FloatingContactIconsNew />
      
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-cyan-500/10 rounded-full blur-[150px]" />
      </div>

      <div className="relative w-full max-w-md my-auto">
        {/* Brand Header */}
        <div className="text-center mb-8">
          <div className="relative inline-flex items-center justify-center mb-4">
            <div className="absolute inset-0 bg-gradient-to-r from-cyan-400 to-blue-500 rounded-2xl blur-xl opacity-40 scale-110" />
            <div className="relative w-16 h-16 rounded-2xl bg-gradient-to-br from-[#0a1628] to-[#061020] border border-cyan-500/40 flex items-center justify-center shadow-xl">
              <Activity className="w-8 h-8 text-cyan-400" />
            </div>
          </div>
          <h1 className="text-3xl font-black bg-gradient-to-r from-white via-cyan-200 to-blue-400 bg-clip-text text-transparent">
            AlphaTradeVission
          </h1>
          <div className="mt-1 flex items-center justify-center gap-2">
            <span className="text-xl font-light text-cyan-400">Ticks</span>
            <span className="px-2 py-0.5 text-[9px] font-bold bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-full uppercase">Pro</span>
          </div>
          
          {/* Premium Slogan */}
          <div className="mt-4 relative">
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-cyan-500/20 to-transparent blur-xl" />
            <div className="relative flex items-center justify-center gap-3">
              <div className="h-[1px] w-8 bg-gradient-to-r from-transparent to-cyan-500/50" />
              <p className="text-[10px] font-semibold uppercase tracking-[0.2em] whitespace-nowrap">
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-200 via-yellow-300 to-amber-200">Find </span>
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-emerald-400 to-cyan-400">Operator Positions</span>
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-200 via-yellow-300 to-amber-200"> with Real Time Data</span>
              </p>
              <div className="h-[1px] w-8 bg-gradient-to-l from-transparent to-cyan-500/50" />
            </div>
          </div>
        </div>

        {/* Auth Card */}
        <div className="relative">
          <div className="absolute -inset-1 bg-gradient-to-r from-cyan-500/20 to-blue-500/20 rounded-2xl blur-lg" />
          <div className="relative bg-[#0a0e17]/90 backdrop-blur-xl border border-[#1a2540] rounded-2xl p-6 max-h-[80vh] overflow-y-auto">
            
            {/* Toggle - 3 Tabs */}
            <div className="flex bg-[#111827] rounded-lg p-1 mb-6">
              <button
                onClick={() => setActiveTab('signin')}
                className={`flex-1 py-2 text-xs font-medium rounded-md transition-all ${
                  activeTab === 'signin' 
                    ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white' 
                    : 'text-[#6b7a99] hover:text-white'
                }`}
                data-testid="tab-signin"
              >
                Sign In
              </button>
              <button
                onClick={() => setActiveTab('signup')}
                className={`flex-1 py-2 text-xs font-medium rounded-md transition-all ${
                  activeTab === 'signup' 
                    ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white' 
                    : 'text-[#6b7a99] hover:text-white'
                }`}
                data-testid="tab-signup"
              >
                Sign Up
              </button>
              <button
                onClick={() => setActiveTab('premium')}
                className={`flex-1 py-2 text-xs font-medium rounded-md transition-all flex items-center justify-center gap-1 ${
                  activeTab === 'premium' 
                    ? 'bg-gradient-to-r from-amber-500 to-orange-600 text-white' 
                    : 'text-amber-400 hover:text-amber-300'
                }`}
                data-testid="tab-premium"
              >
                <Crown className="w-3 h-3" />
                Go Premium
              </button>
            </div>

            {/* Premium Tab Content */}
            {activeTab === 'premium' && <GoPremiumTab />}

            {/* Sign In / Sign Up Form */}
            {activeTab !== 'premium' && (
            <form onSubmit={handleSubmit} className="space-y-3">
              {activeTab === 'signup' && (
                <>
                  <div>
                    <Label className="text-[#6b7a99] text-xs">Full Name *</Label>
                    <div className="relative mt-1">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4a5a7a]" />
                      <Input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className="pl-10 bg-[#111827] border-[#1a2540] text-white rounded-lg h-10"
                        placeholder="Your name"
                        data-testid="signup-name-input"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-[#6b7a99] text-xs">Phone Number *</Label>
                    <div className="relative mt-1">
                      <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4a5a7a]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                      <Input
                        type="tel"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        className="pl-10 bg-[#111827] border-[#1a2540] text-white rounded-lg h-10"
                        placeholder="9876543210"
                        data-testid="signup-phone-input"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-[#6b7a99] text-xs">City</Label>
                      <Input
                        type="text"
                        value={city}
                        onChange={(e) => setCity(e.target.value)}
                        className="mt-1 bg-[#111827] border-[#1a2540] text-white rounded-lg h-10"
                        placeholder="Mumbai"
                        data-testid="signup-city-input"
                      />
                    </div>
                    <div>
                      <Label className="text-[#6b7a99] text-xs">Address</Label>
                      <Input
                        type="text"
                        value={address}
                        onChange={(e) => setAddress(e.target.value)}
                        className="mt-1 bg-[#111827] border-[#1a2540] text-white rounded-lg h-10"
                        placeholder="Your address"
                        data-testid="signup-address-input"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-[#6b7a99] text-xs">Referral/Promo Code (Optional)</Label>
                    <div className="relative mt-1">
                      <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4a5a7a]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <Input
                        type="text"
                        value={referralCode}
                        onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                        className="pl-10 bg-[#111827] border-[#1a2540] text-white rounded-lg h-10"
                        placeholder="Enter code (if any)"
                        data-testid="signup-referral-input"
                      />
                    </div>
                    {referralCodeValid && (
                      <p className="mt-1 text-[10px] text-green-400">{referralCodeValid}</p>
                    )}
                  </div>
                </>
              )}

              <div>
                <Label className="text-[#6b7a99] text-xs">Email *</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4a5a7a]" />
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 bg-[#111827] border-[#1a2540] text-white rounded-lg h-10"
                    placeholder="your@email.com"
                    required
                    data-testid="login-email-input"
                  />
                </div>
              </div>

              <div>
                <Label className="text-[#6b7a99] text-xs">Password *</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4a5a7a]" />
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 pr-10 bg-[#111827] border-[#1a2540] text-white rounded-lg"
                    placeholder="Enter password"
                    required
                    minLength={6}
                    data-testid="login-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#4a5a7a] hover:text-white"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* Terms & Conditions - Only show on signup */}
              {activeTab === 'signup' && (
                <div className="flex items-start gap-2 mt-2">
                  <input
                    type="checkbox"
                    id="terms-checkbox"
                    checked={termsAccepted}
                    onChange={(e) => setTermsAccepted(e.target.checked)}
                    className="mt-1 w-4 h-4 rounded border-[#1a2540] bg-[#111827] text-cyan-500 focus:ring-cyan-500 focus:ring-offset-0 cursor-pointer"
                    data-testid="terms-checkbox"
                  />
                  <label htmlFor="terms-checkbox" className="text-[10px] text-[#6b7a99]">
                    I agree to the{' '}
                    <button
                      type="button"
                      onClick={() => setShowTermsModal(true)}
                      className="text-cyan-400 hover:text-cyan-300 underline"
                      data-testid="terms-link"
                    >
                      Terms & Conditions
                    </button>
                    {' '}and acknowledge all risks associated with trading.
                  </label>
                </div>
              )}

              {error && (
                <div className="flex items-center justify-center gap-2 px-3 py-2 bg-red-500/5 border border-red-500/20 rounded-lg" data-testid="login-error-banner">
                  <AlertCircle className="w-3.5 h-3.5 text-red-400 flex-shrink-0" />
                  <p className="text-xs text-red-400 text-center">{error}</p>
                  {errorDetail && (
                    <button
                      type="button"
                      onClick={() => setShowErrorHelp(true)}
                      className="text-cyan-400 hover:text-cyan-300 transition-colors flex-shrink-0"
                      data-testid="login-error-info-btn"
                      title="Why did this fail? How to fix?"
                    >
                      <Info className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              )}

              <Button
                type="submit"
                disabled={isLoading}
                className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white font-bold py-3 h-12 rounded-xl shadow-lg shadow-cyan-500/30"
                data-testid="login-submit-btn"
              >
                {isLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : activeTab === 'signin' ? (
                  'Sign In'
                ) : (
                  'Create Account'
                )}
              </Button>
              
            </form>
            )}

            {/* Features Preview */}
            {activeTab !== 'premium' && (
            <div className="mt-6 pt-4 border-t border-[#1a2540]">
              <p className="text-[10px] text-[#4a5a7a] text-center mb-3">WHAT YOU GET</p>
              <div className="grid grid-cols-2 gap-2">
                <div className="flex items-center gap-2 p-2 bg-green-500/5 border border-green-500/20 rounded-lg">
                  <Check className="w-3 h-3 text-green-400" />
                  <span className="text-[10px] text-green-400">Live Watchlist</span>
                  <span className="ml-auto text-[8px] text-green-400/60">FREE</span>
                </div>
                <div className="flex items-center gap-2 p-2 bg-amber-500/5 border border-amber-500/20 rounded-lg">
                  <Crown className="w-3 h-3 text-amber-400" />
                  <span className="text-[10px] text-amber-400">Tick Log</span>
                  <span className="ml-auto text-[8px] text-amber-400/60">PRO</span>
                </div>
                <div className="flex items-center gap-2 p-2 bg-amber-500/5 border border-amber-500/20 rounded-lg">
                  <Crown className="w-3 h-3 text-amber-400" />
                  <span className="text-[10px] text-amber-400">History</span>
                  <span className="ml-auto text-[8px] text-amber-400/60">PRO</span>
                </div>
                <div className="flex items-center gap-2 p-2 bg-amber-500/5 border border-amber-500/20 rounded-lg">
                  <Crown className="w-3 h-3 text-amber-400" />
                  <span className="text-[10px] text-amber-400">Scanner</span>
                  <span className="ml-auto text-[8px] text-amber-400/60">PRO</span>
                </div>
              </div>
            </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <p className="mt-6 text-xs text-[#3a4a6a] text-center">
          © 2025 AlphaTradeVission. All rights reserved.
        </p>
        <p className="mt-2 text-[10px] text-[#2a3a5a] text-center">
          Real-time tick data powered by Zerodha Kite Connect
        </p>
        <p className="mt-3 text-sm text-white/70 text-center font-medium tracking-wide">
          Made by Mr.MarketX
        </p>
      </div>

      {/* Admin Panel Modal */}
      <AdminPanel isOpen={showAdminPanel} onClose={() => setShowAdminPanel(false)} />

      {/* Sign-In Error Help Modal */}
      {showErrorHelp && errorDetail && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" data-testid="login-error-help-modal">
          <div className="bg-[#0a0e17] border border-red-500/40 rounded-2xl w-full max-w-md shadow-2xl overflow-hidden">
            {/* Header */}
            <div className="flex items-start justify-between p-5 border-b border-red-500/20 bg-red-500/5">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-red-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <AlertCircle className="w-5 h-5 text-red-400" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-white">{errorDetail.title}</h3>
                  <p className="text-[10px] text-red-300/70 mt-0.5 font-mono">Error code: {errorDetail.code}</p>
                </div>
              </div>
              <button
                onClick={() => setShowErrorHelp(false)}
                className="text-[#6b7a99] hover:text-white p-1 flex-shrink-0"
                data-testid="login-error-help-close-btn"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Body */}
            <div className="p-5 space-y-4">
              {errorDetail.message && (
                <div>
                  <p className="text-[10px] uppercase text-[#6b7a99] tracking-wider mb-1.5">What happened</p>
                  <p className="text-xs text-[#a8b8d8] leading-relaxed">{errorDetail.message}</p>
                </div>
              )}

              <div>
                <p className="text-[10px] uppercase text-[#6b7a99] tracking-wider mb-2">How to fix</p>
                <ul className="space-y-2">
                  {errorDetail.fixes.map((fix, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-xs text-[#c0d0f0]">
                      <span className="flex-shrink-0 w-4 h-4 rounded-full bg-cyan-500/20 text-cyan-400 text-[10px] font-bold flex items-center justify-center mt-0.5">{idx + 1}</span>
                      <span className="leading-relaxed">{fix}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-2 p-4 border-t border-[#1a2540] bg-[#070b13]">
              <Button
                variant="outline"
                className="flex-1 border-[#2a3a5a] text-[#a8b8d8] hover:bg-[#1a2540] text-xs h-9"
                onClick={() => {
                  const diag = JSON.stringify({
                    code: errorDetail.code,
                    title: errorDetail.title,
                    message: errorDetail.message,
                    timestamp: new Date().toISOString(),
                    url: window.location.href,
                    userAgent: navigator.userAgent,
                  }, null, 2);
                  if (navigator.clipboard && window.isSecureContext) {
                    navigator.clipboard.writeText(diag).then(() => toast.success('Diagnostic copied!')).catch(() => toast.error('Copy failed'));
                  } else {
                    toast.error('Copy not supported in this browser');
                  }
                }}
                data-testid="login-error-copy-diag-btn"
              >
                <Copy className="w-3 h-3 mr-1.5" /> Copy diagnostic
              </Button>
              {errorDetail.canRetry ? (
                <Button
                  className="flex-1 bg-cyan-600 hover:bg-cyan-700 text-white text-xs h-9"
                  onClick={() => {
                    setShowErrorHelp(false);
                    setError('');
                    setErrorDetail(null);
                  }}
                  data-testid="login-error-retry-btn"
                >
                  <RefreshCw className="w-3 h-3 mr-1.5" /> Try again
                </Button>
              ) : (
                <Button
                  className="flex-1 bg-cyan-600 hover:bg-cyan-700 text-white text-xs h-9"
                  onClick={() => setShowErrorHelp(false)}
                  data-testid="login-error-ack-btn"
                >
                  Got it
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Security Code Modal */}
      {showSecurityModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm">
          <div className="bg-[#0a0e17] border border-amber-500/40 rounded-2xl p-6 w-80 shadow-2xl">
            <div className="text-center mb-4">
              <div className="w-12 h-12 mx-auto rounded-xl bg-amber-500/20 flex items-center justify-center mb-3">
                <Shield className="w-6 h-6 text-amber-400" />
              </div>
              <h3 className="text-lg font-bold text-white">Admin Security</h3>
              <p className="text-xs text-[#6b7a99] mt-1">Enter security code to access admin panel</p>
            </div>
            
            <Input
              type="password"
              value={securityCodeInput}
              onChange={(e) => setSecurityCodeInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && verifySecurityCode()}
              placeholder="Enter security code..."
              className="bg-[#111827] border-amber-500/30 text-white text-center mb-4"
              autoFocus
            />
            
            <div className="flex gap-2">
              <Button
                onClick={() => setShowSecurityModal(false)}
                variant="outline"
                className="flex-1 border-[#2a3a5a] text-[#6b7a99]"
              >
                Cancel
              </Button>
              <Button
                onClick={verifySecurityCode}
                className="flex-1 bg-amber-500 hover:bg-amber-600 text-white"
              >
                Verify
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Terms & Conditions Modal */}
      {showTermsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
          <div className="bg-[#0a1628] border border-[#1a2540] rounded-2xl w-full max-w-2xl max-h-[85vh] flex flex-col shadow-2xl">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-[#1a2540]">
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-cyan-400" />
                <h2 className="text-lg font-bold text-white">Terms & Conditions</h2>
              </div>
              <button
                onClick={() => setShowTermsModal(false)}
                className="text-[#6b7a99] hover:text-white p-1"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 text-sm text-[#a0aec0]">
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
                <p className="text-amber-400 font-semibold text-xs">IMPORTANT DISCLAIMER</p>
                <p className="text-[11px] mt-1">Please read these terms carefully before using AlphaTradeVission. By creating an account, you agree to be bound by these terms.</p>
              </div>

              <div className="space-y-3">
                <div className="bg-[#111827] rounded-lg p-3">
                  <h3 className="text-cyan-400 font-semibold text-xs mb-2">1. NO REFUND POLICY</h3>
                  <p className="text-[11px] leading-relaxed">All subscription fees and payments made to AlphaTradeVission are <span className="text-red-400 font-semibold">non-refundable</span>. Once a payment is processed, no refunds will be issued under any circumstances, including but not limited to:</p>
                  <ul className="list-disc list-inside text-[10px] mt-2 space-y-1 text-[#8a9ab0]">
                    <li>Change of mind or dissatisfaction with services</li>
                    <li>Technical issues on user's end</li>
                    <li>Failure to use the service during subscription period</li>
                    <li>Account suspension due to policy violations</li>
                  </ul>
                </div>

                <div className="bg-[#111827] rounded-lg p-3">
                  <h3 className="text-cyan-400 font-semibold text-xs mb-2">2. NO GUARANTEE OF SUCCESS OR PROFITS</h3>
                  <p className="text-[11px] leading-relaxed">AlphaTradeVission is a market data visualization and alerting tool. We <span className="text-red-400 font-semibold">do not guarantee</span>:</p>
                  <ul className="list-disc list-inside text-[10px] mt-2 space-y-1 text-[#8a9ab0]">
                    <li>Any trading profits or returns on investment</li>
                    <li>Success in any trading strategies</li>
                    <li>Accuracy of any signals, alerts, or AI-generated content</li>
                    <li>Future performance based on historical data</li>
                  </ul>
                  <p className="text-[10px] mt-2 text-amber-400">Trading in financial markets involves substantial risk. Past performance is not indicative of future results.</p>
                </div>

                <div className="bg-[#111827] rounded-lg p-3">
                  <h3 className="text-cyan-400 font-semibold text-xs mb-2">3. DATA ACCURACY DISCLAIMER</h3>
                  <p className="text-[11px] leading-relaxed">While we strive to provide accurate data, we <span className="text-red-400 font-semibold">cannot guarantee</span>:</p>
                  <ul className="list-disc list-inside text-[10px] mt-2 space-y-1 text-[#8a9ab0]">
                    <li>100% accuracy of market data, tick logs, or historical records</li>
                    <li>Real-time data without any delays</li>
                    <li>Data completeness or absence of errors</li>
                    <li>Correctness of calculated values (spreads, volumes, etc.)</li>
                  </ul>
                  <p className="text-[10px] mt-2 text-[#8a9ab0]">Data is sourced from third-party providers (Zerodha/Kite Connect) and may contain errors or delays beyond our control.</p>
                </div>

                <div className="bg-[#111827] rounded-lg p-3">
                  <h3 className="text-cyan-400 font-semibold text-xs mb-2">4. TECHNICAL ISSUES & SERVICE AVAILABILITY</h3>
                  <p className="text-[11px] leading-relaxed">We acknowledge that technical issues may occur:</p>
                  <ul className="list-disc list-inside text-[10px] mt-2 space-y-1 text-[#8a9ab0]">
                    <li>Server downtime or maintenance windows</li>
                    <li>WebSocket connection failures or data stream interruptions</li>
                    <li>Browser compatibility issues</li>
                    <li>Network-related problems</li>
                    <li>Third-party API failures (Zerodha, Telegram, etc.)</li>
                  </ul>
                  <p className="text-[10px] mt-2 text-amber-400">We are not liable for any losses incurred due to technical issues or service unavailability.</p>
                </div>

                <div className="bg-[#111827] rounded-lg p-3">
                  <h3 className="text-cyan-400 font-semibold text-xs mb-2">5. USER RESPONSIBILITIES</h3>
                  <p className="text-[11px] leading-relaxed">As a user, you agree to:</p>
                  <ul className="list-disc list-inside text-[10px] mt-2 space-y-1 text-[#8a9ab0]">
                    <li>Provide accurate personal information</li>
                    <li>Maintain confidentiality of your account credentials</li>
                    <li>Use the service for personal, non-commercial purposes only</li>
                    <li>Not attempt to reverse-engineer or exploit the platform</li>
                    <li>Accept full responsibility for your trading decisions</li>
                  </ul>
                </div>

                <div className="bg-[#111827] rounded-lg p-3">
                  <h3 className="text-cyan-400 font-semibold text-xs mb-2">6. LIMITATION OF LIABILITY</h3>
                  <p className="text-[11px] leading-relaxed">AlphaTradeVission and its operators shall not be liable for:</p>
                  <ul className="list-disc list-inside text-[10px] mt-2 space-y-1 text-[#8a9ab0]">
                    <li>Any direct, indirect, or consequential trading losses</li>
                    <li>Missed trading opportunities due to service issues</li>
                    <li>Decisions made based on data or alerts from the platform</li>
                    <li>Any damages arising from use or inability to use the service</li>
                  </ul>
                </div>

                <div className="bg-[#111827] rounded-lg p-3">
                  <h3 className="text-cyan-400 font-semibold text-xs mb-2">7. ACCOUNT TERMINATION</h3>
                  <p className="text-[11px] leading-relaxed">We reserve the right to suspend or terminate accounts:</p>
                  <ul className="list-disc list-inside text-[10px] mt-2 space-y-1 text-[#8a9ab0]">
                    <li>For violation of these terms</li>
                    <li>For suspected fraudulent activity</li>
                    <li>At our sole discretion without prior notice</li>
                  </ul>
                  <p className="text-[10px] mt-2 text-red-400">No refunds will be provided for terminated accounts.</p>
                </div>

                <div className="bg-[#111827] rounded-lg p-3">
                  <h3 className="text-cyan-400 font-semibold text-xs mb-2">8. MODIFICATIONS TO TERMS</h3>
                  <p className="text-[11px] leading-relaxed">We may modify these terms at any time. Continued use of the service after modifications constitutes acceptance of the updated terms.</p>
                </div>
              </div>

              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mt-4">
                <p className="text-red-400 font-semibold text-xs">RISK WARNING</p>
                <p className="text-[11px] mt-1">Trading in stocks, futures, and options involves substantial risk of loss and is not suitable for all investors. You should carefully consider whether trading is appropriate for you in light of your financial condition. Only risk capital should be used for trading.</p>
              </div>
            </div>
            
            {/* Footer */}
            <div className="p-4 border-t border-[#1a2540]">
              <Button
                onClick={() => {
                  setTermsAccepted(true);
                  setShowTermsModal(false);
                }}
                className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white font-bold py-3 h-11 rounded-xl"
                data-testid="accept-terms-btn"
              >
                <Check className="w-4 h-4 mr-2" />
                I Accept These Terms & Conditions
              </Button>
              <p className="text-[9px] text-center text-[#6b7a99] mt-2">
                By clicking accept, you acknowledge that you have read and understood these terms.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
